import { useEffect, useState, createContext, useContext, useRef, useMemo } from "react";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import api, { API } from "./api";
import "./App.css";
import { Toaster, toast } from "sonner";
import {
  Building2, Users, Briefcase, FileText, UserCircle2, LogOut, Search, Plus, Edit3, Trash2,
  Camera, MapPin, Star, X, Upload, Download, Filter, Eraser, Phone, Mail, Calendar,
  Settings as SettingsIcon, Image as ImgIcon, FileScan, Inbox, ListChecks, KeyRound
} from "lucide-react";

// ===== Auth context =====
const AuthCtx = createContext(null);
const useAuth = () => useContext(AuthCtx);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  useEffect(() => { api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(false)); }, []);
  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem("be_token", r.data.token);
    const me = await api.get("/auth/me"); setUser(me.data);
  };
  const logout = async () => { try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("be_token"); setUser(false); };
  return <AuthCtx.Provider value={{ user, login, logout, setUser }}>{children}</AuthCtx.Provider>;
}
function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ===== UnsavedGuard: warns on tab close =====
function useUnsavedGuard(dirty) {
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);
}

// ===== Draft autosave (localStorage), keyed by name =====
function useDraft(key, value, setValue, enabled = true) {
  // Try restore on mount
  useEffect(() => {
    if (!enabled) return;
    try {
      const s = localStorage.getItem("draft-" + key);
      if (s) {
        const parsed = JSON.parse(s);
        if (parsed && Object.keys(parsed).length > 0 &&
            window.confirm("Resume your unsaved entry for " + key + "?")) {
          setValue(parsed);
        } else {
          localStorage.removeItem("draft-" + key);
        }
      }
    } catch {}
    // eslint-disable-next-line
  }, []);
  // Auto-persist on change (debounced)
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      try { localStorage.setItem("draft-" + key, JSON.stringify(value)); } catch {}
    }, 800);
    return () => clearTimeout(t);
  }, [value, key, enabled]);
}
function clearDraft(key) { try { localStorage.removeItem("draft-" + key); } catch {} }

// ===== Clearable input wrapper (small × inside) =====
function SearchInput({ value, onChange, onSubmit, placeholder, testid }) {
  return (
    <div className="search-wrap">
      <input value={value} onChange={e=>onChange(e.target.value)}
        onKeyDown={e=>e.key==="Enter" && onSubmit && onSubmit()}
        placeholder={placeholder} data-testid={testid}/>
      {value && <button className="search-clear" onClick={()=>{ onChange(""); onSubmit && setTimeout(onSubmit, 0); }} aria-label="Clear">×</button>}
    </div>
  );
}

// ===== Login =====
function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("aditya@ivoryandvineweddings.com");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  if (user) return <Navigate to="/" replace />;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await login(email, password); nav("/"); }
    catch (e) { setErr(e?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={submit}>
        <img src="/logo.png" alt="Ivory & Vine" className="login-logo"/>
        <div className="brand-sub">Wedding Planner CRM</div>
        <label>Email</label>
        <input data-testid="login-email" value={email} onChange={e=>setEmail(e.target.value)} type="email" required/>
        <label>Password</label>
        <input data-testid="login-password" value={password} onChange={e=>setPassword(e.target.value)} type="password" required/>
        {err && <div className="err">{err}</div>}
        <button data-testid="login-submit" disabled={loading} type="submit">{loading?"Signing in…":"Sign in"}</button>
      </form>
    </div>
  );
}

// ===== Lookups hook (cascading) =====
function useLookups(type, parent) {
  const [list, setList] = useState([]);
  const refresh = async () => {
    try {
      const params = { type };
      if (parent) params.parent = parent;
      const r = await api.get("/lookups", { params });
      // Backend returns matching-parent + global (no parent). Prefer matching-parent if any exist.
      let items = r.data;
      if (parent) {
        const matching = items.filter(x => x.parent === parent);
        items = matching.length ? matching : items.filter(x => !x.parent);
      }
      setList(items);
    } catch (e) { console.warn("lookups load failed", type, e?.response?.status); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [type, parent]);
  const add = async (value) => {
    try {
      const r = await api.post("/lookups", { type, value, parent: parent || null });
      await refresh(); return r.data;
    } catch (e) { return null; }
  };
  return { list, add, refresh };
}

// ===== Typeahead =====
function Typeahead({ type, parent, value, onChange, placeholder, testid }) {
  const { list, add } = useLookups(type, parent);
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(() => setQ(value || ""), [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = list.filter(x => x.value.toLowerCase().includes((q||"").toLowerCase()));
  const exact = list.find(x => x.value.toLowerCase() === (q||"").toLowerCase());
  return (
    <div className="ta" ref={ref}>
      <input data-testid={testid} value={q} onChange={e=>{setQ(e.target.value); setOpen(true);}}
             onFocus={()=>setOpen(true)} placeholder={placeholder||"Type to search or add new…"}
             onBlur={()=>{ setTimeout(()=>{ if(q !== value) onChange(q); }, 200); }}/>
      {open && (
        <div className="ta-menu">
          {filtered.slice(0,40).map(x => (
            <div key={x.id} className="ta-item" onMouseDown={()=>{ onChange(x.value); setQ(x.value); setOpen(false); }}>{x.value}</div>
          ))}
          {q && !exact && (
            <div className="ta-add" onMouseDown={async ()=>{ await add(q); onChange(q); setOpen(false); }}>
              <Plus size={14}/> Add "{q}" as new {type.replace(/_/g," ")}
            </div>
          )}
          {!q && <div className="ta-hint">💡 Type a new {type.replace(/_/g," ")} name to add it</div>}
        </div>
      )}
    </div>
  );
}

// ===== Multi-select =====
function MultiSelect({ type, parent, value=[], onChange }) {
  const { list, add } = useLookups(type, parent);
  const [q, setQ] = useState(""); const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(()=>{ const h=(e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false);};
    document.addEventListener("mousedown",h); return ()=>document.removeEventListener("mousedown",h); },[]);
  const toggle = (v) => onChange(value.includes(v) ? value.filter(x=>x!==v) : [...value, v]);
  const filtered = list.filter(x => x.value.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="ms" ref={ref}>
      <div className="ms-input" onClick={()=>setOpen(true)}>
        {value.map(v => <span key={v} className="chip">{v}<X size={12} onClick={(e)=>{e.stopPropagation();toggle(v);}}/></span>)}
        <input value={q} onChange={e=>{setQ(e.target.value); setOpen(true);}} placeholder={value.length?"Type to add…":"Select or type to add new…"}/>
      </div>
      {open && (
        <div className="ta-menu">
          {filtered.map(x=> (
            <div key={x.id} className="ta-item" onMouseDown={()=>toggle(x.value)}>
              <input type="checkbox" readOnly checked={value.includes(x.value)}/> {x.value}
            </div>
          ))}
          {q && !list.find(x=>x.value.toLowerCase()===q.toLowerCase()) && (
            <div className="ta-add" onMouseDown={async ()=>{ await add(q); toggle(q); setQ(""); }}>
              <Plus size={14}/> Add "{q}" as new {type.replace(/_/g," ")}
            </div>
          )}
          {!q && <div className="ta-hint">💡 Type a new {type.replace(/_/g," ")} name to add it</div>}
        </div>
      )}
    </div>
  );
}

// ===== Searchable Select (typeahead for any list of {id, label}) =====
function SearchableSelect({ items, value, onChange, placeholder, testid }) {
  const [q, setQ] = useState(""); const [open, setOpen] = useState(false);
  const ref = useRef();
  useEffect(()=>{ const h=(e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false);};
    document.addEventListener("mousedown",h); return ()=>document.removeEventListener("mousedown",h); },[]);
  const selected = items.find(x => x.id === value);
  const ql = q.trim().toLowerCase();
  const tokens = ql ? ql.split(/\s+/) : [];
  const filtered = !tokens.length ? items :
    items.filter(x => tokens.every(t => (x.label||"").toLowerCase().includes(t)));
  return (
    <div className="ta" ref={ref} style={{flex:1, minWidth:240}}>
      <input data-testid={testid} value={open ? q : (selected?.label || "")}
        onChange={e=>{ setQ(e.target.value); setOpen(true); }}
        onFocus={()=>{ setQ(""); setOpen(true); }}
        placeholder={placeholder||"Type to search…"}/>
      {open && (
        <div className="ta-menu" style={{maxHeight:280, overflowY:"auto"}}>
          {filtered.slice(0,100).map(x => (
            <div key={x.id} className="ta-item" onMouseDown={()=>{ onChange(x.id); setOpen(false); setQ(""); }}>
              {x.label}
            </div>
          ))}
          {filtered.length === 0 && <div className="ta-empty">No matches for "{q}"</div>}
          {filtered.length > 100 && <div className="ta-empty">{filtered.length-100} more matches — type to narrow…</div>}
        </div>
      )}
    </div>
  );
}

// ===== Global header search =====
function GlobalSearch() {
  const [q, setQ] = useState("");
  const [r, setR] = useState(null);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const ref = useRef();
  useEffect(() => {
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setR(null); return; }
      try { const res = await api.get("/search", { params: { q } }); setR(res.data); setOpen(true); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(()=>{ const h=(e)=>{ if(ref.current && !ref.current.contains(e.target)) setOpen(false);};
    document.addEventListener("mousedown",h); return ()=>document.removeEventListener("mousedown",h); },[]);
  const totalCount = r ? (r.hotels.length+r.contacts.length+r.vendors.length+r.clients.length+(r.vendor_contacts?.length||0)+(r.inventory?.length||0)) : 0;
  return (
    <div className="gsearch" ref={ref}>
      <Search size={14} className="gsi"/>
      <input data-testid="global-search" placeholder="Search everything…" value={q}
             onChange={e=>setQ(e.target.value)} onFocus={()=>r && setOpen(true)}/>
      {open && r && totalCount > 0 && (
        <div className="gsearch-menu">
          {r.hotels.length > 0 && <div className="gs-section">Hotels</div>}
          {r.hotels.map(h => <div key={h.id} className="gs-item" onMouseDown={()=>{nav("/"); setOpen(false); setQ("");}}>
            <Building2 size={12}/> <b>{h.venue_name}</b> <span className="muted">— {h.city}, {h.brand}</span></div>)}
          {r.contacts.length > 0 && <div className="gs-section">Hotel Contacts</div>}
          {r.contacts.map(c => <div key={c.id} className="gs-item" onMouseDown={()=>{nav("/contacts"); setOpen(false); setQ("");}}>
            <Users size={12}/> {c.name} <span className="muted">— {c.designation}, {c.hotel_name}</span></div>)}
          {r.vendors.length > 0 && <div className="gs-section">Vendors</div>}
          {r.vendors.map(v => <div key={v.id} className="gs-item" onMouseDown={()=>{nav("/vendors"); setOpen(false); setQ("");}}>
            <Briefcase size={12}/> {v.name} <span className="muted">— {v.category}, {v.city}</span></div>)}
          {r.vendor_contacts?.length > 0 && <div className="gs-section">Vendor Contacts</div>}
          {r.vendor_contacts?.map(vc => <div key={vc.id} className="gs-item" onMouseDown={()=>{nav("/vendors"); setOpen(false); setQ("");}}>
            <Users size={12}/> {vc.name} <span className="muted">— {vc.designation}</span></div>)}
          {r.inventory?.length > 0 && <div className="gs-section">Inventory / Roster</div>}
          {r.inventory?.map(inv => <div key={inv.id} className="gs-item" onMouseDown={()=>{nav("/vendors"); setOpen(false); setQ("");}}>
            <span style={{fontSize:11}}>🎤</span> <b>{inv.item}</b> <span className="muted">— {[inv.category, inv.sub_category, inv.specialty].filter(Boolean).join(" / ")}{inv.vendor_name?` · ${inv.vendor_name}`:""}</span></div>)}
          {r.clients.length > 0 && <div className="gs-section">Clients</div>}
          {r.clients.map(cl => <div key={cl.id} className="gs-item" onMouseDown={()=>{nav("/clients"); setOpen(false); setQ("");}}>
            <UserCircle2 size={12}/> {cl.name} <span className="muted">— {cl.event_type}</span></div>)}
        </div>
      )}
      {open && r && totalCount === 0 && q.length >= 2 && <div className="gsearch-menu"><div className="gs-empty">No matches</div></div>}
    </div>
  );
}

// ===== Reminder Banner & Recently Viewed =====
function ReminderBanner() {
  const [events, setEvents] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("rb-dismissed") === "1"; } catch { return false; }
  });
  const [open, setOpen] = useState(false);
  useEffect(()=>{ api.get("/calendar/upcoming", { params: { days: 7 }}).then(r=>setEvents(r.data||[])).catch(()=>{}); }, []);
  if (dismissed || events.length === 0) return null;
  const bd = events.filter(e=>e.type==="Birthday" || e.type==="Spouse Birthday").length;
  const an = events.filter(e=>e.type==="Anniversary").length;
  return (
    <div className="reminder-banner" data-testid="reminder-banner">
      <button className="rb-main" onClick={()=>setOpen(!open)} data-testid="reminder-banner-toggle">
        <Calendar size={14}/>
        <span>
          {bd > 0 && <b>🎂 {bd} birthday{bd===1?"":"s"}</b>}
          {bd > 0 && an > 0 && <span className="muted"> • </span>}
          {an > 0 && <b>💍 {an} anniversar{an===1?"y":"ies"}</b>}
          <span className="muted"> in the next 7 days</span>
        </span>
      </button>
      <button className="rb-close" onClick={()=>{ setDismissed(true); try { sessionStorage.setItem("rb-dismissed","1"); } catch {} }} aria-label="Dismiss">×</button>
      {open && (
        <div className="rb-list">
          {events.slice(0, 12).map(e => (
            <div key={e.contact_id + e.type} className="rb-row">
              <span className="rb-icon">{e.type === "Anniversary" ? "💍" : "🎂"}</span>
              <span className="rb-name">{e.name}</span>
              <span className="muted small">{e.type}{e.hotel_name?` • ${e.hotel_name}`:""}</span>
              <span className="rb-when">{e.in_days === 0 ? "today" : e.in_days === 1 ? "tomorrow" : `in ${e.in_days} days`}</span>
            </div>
          ))}
          {events.length > 12 && <div className="muted small" style={{padding:"6px 10px"}}>+{events.length-12} more</div>}
        </div>
      )}
    </div>
  );
}

function RecentlyViewed({ kind, onPick }) {
  const [items, setItems] = useState([]);
  useEffect(()=>{ api.get(`/recent/${kind}`, { params: { limit: 5 }}).then(r=>setItems(r.data||[])).catch(()=>{}); }, [kind]);
  if (!items.length) return null;
  const label = (it) => kind === "hotels" ? it.venue_name : it.name;
  return (
    <div className="recent-viewed" data-testid={`recent-${kind}`}>
      <div className="rv-title">Recently viewed</div>
      <div className="rv-chips">
        {items.map(it => (
          <button key={it.id} className="rv-chip" onClick={()=>onPick && onPick(it)} title={label(it)}>
            {label(it)}
          </button>
        ))}
      </div>
    </div>
  );
}

// ===== Layout =====
function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const tabs = [
    { to: "/", label: "Hotels", icon: <Building2 size={16}/> },
    { to: "/contacts", label: "Contacts", icon: <Users size={16}/> },
    { to: "/vendors", label: "Vendors", icon: <Briefcase size={16}/> },
    { to: "/clients", label: "Clients", icon: <UserCircle2 size={16}/> },
    { to: "/quotes", label: "Quotes", icon: <FileText size={16}/> },
    { to: "/scan-inbox", label: "Scan", icon: <Inbox size={16}/> },
    { to: "/calendar", label: "Calendar", icon: <Calendar size={16}/> },
    { to: "/map", label: "Map", icon: <MapPin size={16}/> },
  ];
  return (
    <div className="app">
      <header className="hdr">
        <img src="/logo.png" alt="logo" className="hdr-logo"/>
        <nav className="hdr-tabs">
          {tabs.map(t => (
            <Link key={t.to} to={t.to} className={loc.pathname===t.to?"active":""} data-testid={`nav-${t.label.toLowerCase()}`}>
              {t.icon}<span>{t.label}</span>
            </Link>
          ))}
        </nav>
        <GlobalSearch/>
        <div className="hdr-right">
          <span className="user-name">{user.name}</span>
          {user.role==="admin" && <Link to="/settings" className="settings-link"><SettingsIcon size={13}/></Link>}
          <button onClick={logout} className="logout-btn" data-testid="logout-btn"><LogOut size={14}/></button>
        </div>
      </header>
      <ReminderBanner/>
      <main>{children}</main>
    </div>
  );
}

// ===== Reusable Field =====
function Field({ label, children, full }) {
  return <div className={"fld "+(full?"full":"")}><label>{label}</label>{children}</div>;
}

// time-ago helper
function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso); const now = new Date();
    const sec = Math.floor((now - d) / 1000);
    if (sec < 60) return "just now";
    if (sec < 3600) return Math.floor(sec/60)+"m ago";
    if (sec < 86400) return Math.floor(sec/3600)+"h ago";
    const days = Math.floor(sec/86400);
    if (days < 7) return days+"d ago";
    if (days < 30) return Math.floor(days/7)+"w ago";
    return d.toLocaleDateString("en-GB", { day:"numeric", month:"short", year:"numeric" });
  } catch { return ""; }
}

// confirm delete
async function confirmDelete(label, doDelete) {
  if (!window.confirm(`Delete ${label}? You can restore it later from Settings → Maintenance.`)) return;
  try { await doDelete(); toast.success(`${label} deleted`); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
}

function Modal({ title, onClose, children, wide, dirty }) {
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") {
        if (dirty && !window.confirm("You have unsaved changes. Discard and close?")) return;
        onClose();
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [dirty, onClose]);
  const handleClose = () => {
    if (dirty && !window.confirm("You have unsaved changes. Discard and close?")) return;
    onClose();
  };
  return (
    <div className="modal-bg" onClick={handleClose}>
      <div className={"modal "+(wide?"wide":"")} onClick={e=>e.stopPropagation()}>
        <div className="modal-h"><span>{title}</span><X onClick={handleClose} style={{cursor:"pointer"}}/></div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ===== Hotels page =====
function Hotels() {
  const [hotels, setHotels] = useState([]);
  const [filters, setFilters] = useState({ q:"", country:"", state:"", city:"", brand:"", category:"", rooms_min:"", rooms_max:"", preferred:false });
  const [selected, setSelected] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [bulkScan, setBulkScan] = useState(null);  // {batching, results}
  const load = async () => {
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([k,v])=>v!==""&&v!==false));
      const r = await api.get("/hotels", { params });
      setHotels(r.data);
      if (r.data.length && !selected) setSelected(r.data[0]);
      if (selected && !r.data.find(h=>h.id===selected.id)) setSelected(r.data[0]||null);
    } catch (e) { console.warn(e?.response?.status); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const clearF = () => { setFilters({ q:"", country:"", state:"", city:"", brand:"", category:"", rooms_min:"", rooms_max:"", preferred:false }); setTimeout(load, 0); };
  const onBulkUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > 5) { toast.error("Max 5 fact sheets per batch"); return; }
    // Open the modal in "batching" state so the user sees progress
    setBulkScan({batching: true, results: [], total: files.length, done: 0, errors: 0});
    // Process files one at a time using the SINGLE-file endpoint to avoid proxy timeout
    const results = [];
    let done = 0, errors = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/ocr/fact-sheet", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,  // 2 min per file
        });
        const d = r.data || {};
        d._filename = file.name;
        d._fileRef = file;  // kept for retry
        d._ok = true;
        results.push(d);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.response?.statusText || e?.message || "scan failed";
        results.push({_filename: file.name, _fileRef: file, _ok: false, _error: String(msg).slice(0, 300)});
        errors++;
      }
      done++;
      setBulkScan(prev => prev ? {...prev, done, errors, results: [...results]} : prev);
    }
    setBulkScan({batching: false, results, total: files.length, done, errors});
  };
  return (
    <div className="three-col">
      <aside className="sidebar">
        <div className="side-h"><Filter size={14}/> Filters</div>
        <input className="ipt" placeholder="Search…" value={filters.q} onChange={e=>setFilters({...filters,q:e.target.value})} onKeyDown={e=>e.key==="Enter"&&load()} data-testid="hotel-search"/>
        <Typeahead type="country" value={filters.country} onChange={v=>setFilters({...filters,country:v})} placeholder="Country"/>
        <Typeahead type="state" parent={filters.country} value={filters.state} onChange={v=>setFilters({...filters,state:v})} placeholder="State"/>
        <Typeahead type="city" parent={filters.state} value={filters.city} onChange={v=>setFilters({...filters,city:v})} placeholder="City"/>
        <Typeahead type="brand" value={filters.brand} onChange={v=>setFilters({...filters,brand:v})} placeholder="Brand"/>
        <Typeahead type="category" value={filters.category} onChange={v=>setFilters({...filters,category:v})} placeholder="Category"/>
        <div className="row2">
          <input className="ipt" type="number" placeholder="Min rooms" value={filters.rooms_min} onChange={e=>setFilters({...filters,rooms_min:e.target.value})}/>
          <input className="ipt" type="number" placeholder="Max" value={filters.rooms_max} onChange={e=>setFilters({...filters,rooms_max:e.target.value})}/>
        </div>
        <label className="chk"><input type="checkbox" checked={filters.preferred} onChange={e=>setFilters({...filters,preferred:e.target.checked})}/> Preferred only ⭐</label>
        <div className="row2">
          <button className="btn-primary" onClick={load} data-testid="apply-filters"><Filter size={14}/> Apply</button>
          <button className="btn-danger" onClick={clearF}><Eraser size={14}/></button>
        </div>
        <div className="results-h"><span>Results ({hotels.length})</span>
          <div className="row-btns">
            <label className="btn-secondary sm" data-testid="bulk-factsheet-upload" title="Upload up to 5 fact sheets at once">
              <FileScan size={13}/> Bulk
              <input type="file" accept="application/pdf,image/*" multiple hidden disabled={bulkScan?.batching}
                onChange={e=>{onBulkUpload(e.target.files); e.target.value="";}}/>
            </label>
            <button className="btn-primary sm" onClick={()=>setShowAdd(true)} data-testid="add-hotel"><Plus size={14}/> Add</button>
          </div>
        </div>
        <RecentlyViewed kind="hotels" onPick={(h)=>setSelected(h)}/>
        <div className="cards">
          {hotels.map(h => (
            <div key={h.id} className={"card "+(selected?.id===h.id?"sel":"")} onClick={()=>setSelected(h)} data-testid={`hotel-card-${h.id}`}>
              <div className="card-title">{h.venue_name || "(no name)"} {h.preferred && <Star size={12} className="star"/>}</div>
              <div className="card-sub">{[h.city, h.state].filter(Boolean).join(", ")}</div>
              <div className="card-sub">{h.brand}{h.sub_brand?` • ${h.sub_brand}`:""}{h.no_of_rooms?` • ${h.no_of_rooms}r`:""}</div>
              <div className="tags">
                {(h.types||[]).slice(0,2).map(t=><span key={t} className="tag dark">{t}</span>)}
                {(h.ratings||[]).map(t=><span key={t} className="tag lime">{t}</span>)}
              </div>
              <div className="card-foot">Updated {timeAgo(h.updated_at)}</div>
            </div>
          ))}
        </div>
      </aside>
      <section className="center">
        {selected ? <HotelDetail hotel={selected} onUpdate={load}/> : <div className="empty">Select or add a hotel</div>}
      </section>
      {showAdd && <HotelForm onClose={()=>setShowAdd(false)} onSaved={(h)=>{ setShowAdd(false); load(); setSelected(h); }}/>}
      {bulkScan && <BulkFactSheetReview state={bulkScan} onClose={()=>setBulkScan(null)} onDone={()=>{setBulkScan(null); load();}}/>}
    </div>
  );
}

// ============ BULK FACT-SHEET REVIEW MODAL ============
function BulkFactSheetReview({ state, onClose, onDone }) {
  const [keep, setKeep] = useState(()=>state.results?.map(()=>true) || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(0);
  const [retrying, setRetrying] = useState(null);
  // Sync keep array length when results grow (during batching) or after retry
  useEffect(()=>{
    if (state.results && state.results.length !== keep.length) {
      setKeep(state.results.map((_,i)=>keep[i] ?? true));
    }
    /* eslint-disable-next-line */
  }, [state.results?.length]);
  if (state.batching) {
    const pct = state.total ? Math.round((state.done / state.total) * 100) : 0;
    return <Modal title="Bulk Scan in progress…" onClose={onClose}>
      <p><b>Scanning {state.done}/{state.total} fact sheet{state.total>1?"s":""}…</b>
        {state.errors > 0 && <span className="muted small"> · {state.errors} failed so far</span>}
      </p>
      <div style={{height:8, background:"#e5e7eb", borderRadius:4, overflow:"hidden", margin:"10px 0"}}>
        <div style={{height:"100%", width:`${pct}%`, background:"var(--burgundy)", transition:"width 0.3s"}}></div>
      </div>
      <p className="muted small">Tip: Gemini OCR takes ~10-15s per fact sheet — please don't close this window.</p>
      {state.done > 0 && state.results?.length > 0 && (
        <div className="muted small" style={{marginTop:10, padding:"6px 8px", background:"var(--tint)", borderRadius:4}}>
          Completed: {state.results.filter(r=>r._ok).map(r => r.venue_name || r._filename).join(", ") || "—"}
        </div>
      )}
    </Modal>;
  }
  const ok = (state.results || []).filter(r => r._ok);
  const failed = (state.results || []).filter(r => !r._ok);
  const saveAll = async () => {
    setSaving(true);
    let done = 0;
    for (let i = 0; i < state.results.length; i++) {
      if (!keep[i] || !state.results[i]._ok) continue;
      const d = state.results[i];
      const payload = {...d};
      delete payload._ok; delete payload._filename; delete payload._error;
      // Strip sub-arrays — those need separate calls
      const subSpaces = payload.spaces || [];
      const subRooms = payload.rooms || [];
      const subDining = payload.dining || [];
      delete payload.spaces; delete payload.rooms; delete payload.dining;
      try {
        const r = await api.post("/hotels", payload);
        const hid = r.data.id;
        // Add sub-records
        for (const sp of subSpaces) { if (sp.name) await api.post(`/hotels/${hid}/spaces`, sp).catch(()=>{}); }
        for (const rm of subRooms) { if (rm.room_type) await api.post(`/hotels/${hid}/rooms`, rm).catch(()=>{}); }
        for (const dn of subDining) { if (dn.name) await api.post(`/hotels/${hid}/dining`, dn).catch(()=>{}); }
        done++;
      } catch (e) { /* skip on error */ }
    }
    setSaved(done);
    setSaving(false);
    toast.success(`Saved ${done} hotels with sub-records`);
    onDone();
  };
  const retryFile = async (failedIdx) => {
    const failed = state.results[failedIdx];
    if (!failed?._fileRef) {
      toast.error("Original file no longer available — please use single 'Scan Fact Sheet' from the Hotel form.");
      return;
    }
    setRetrying(failedIdx);
    const fd = new FormData();
    fd.append("file", failed._fileRef);
    try {
      const r = await api.post("/ocr/fact-sheet", fd, { timeout: 120000 });
      const d = r.data || {};
      d._filename = failed._filename;
      d._ok = true;
      // Replace the failed row with the successful one
      state.results[failedIdx] = d;
      toast.success(`Re-scanned: ${d.venue_name || failed._filename}`);
      // Force a re-render via dummy state change
      setKeep(k=>{ const n=[...k]; n[failedIdx]=true; return n; });
    } catch (e) {
      toast.error(`Retry failed: ${e?.response?.data?.detail || e?.message}`);
    } finally {
      setRetrying(null);
    }
  };
  return (
    <Modal title={`Review Scan Results (${ok.length} ok${failed.length?`, ${failed.length} failed`:""})`} onClose={onClose} wide>
      {failed.length > 0 && (
        <div className="panel" style={{padding:"10px 12px", background:"#fee2e2", color:"#991b1b", marginBottom:10, borderRadius:6}}>
          <div style={{fontWeight:600, marginBottom:6}}>⚠️ {failed.length} file(s) failed to scan</div>
          <table className="tbl" style={{fontSize:12}}>
            <thead><tr><th>File</th><th>Error</th><th></th></tr></thead>
            <tbody>{state.results.map((r, i) => r._ok ? null : (
              <tr key={i}>
                <td style={{color:"#991b1b"}}>{r._filename}</td>
                <td className="small" style={{color:"#991b1b", maxWidth:400, wordBreak:"break-word"}}>{r._error || "(no error message)"}</td>
                <td>
                  {r._fileRef && <button className="btn-ghost sm" disabled={retrying===i}
                    onClick={()=>retryFile(i)} data-testid={`retry-fs-${i}`}>
                    {retrying===i ? "Retrying…" : "↻ Retry"}
                  </button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
          <div className="small" style={{marginTop:8, color:"#7c2d12"}}>
            💡 Common causes: file is corrupted or password-protected · scanned at very low resolution · Gemini API hit a rate limit. <b>Try again</b> using the button above, or open <i>+ Add Hotel → Scan Fact Sheet</i> for a single-file retry.
          </div>
        </div>
      )}
      {ok.length === 0 && <div className="empty">No successful scans. Check the file types (PDF/image) and try again.</div>}
      {ok.length > 0 && (<>
        <p className="muted small">Tick the ones to save. You can fine-tune each hotel later by editing it.</p>
        <table className="tbl"><thead><tr>
          <th style={{width:30}}>Keep</th><th>Hotel Name</th><th>Brand</th><th>City</th>
          <th>Rooms</th><th>Spaces</th><th>Dining</th><th>File</th>
        </tr></thead><tbody>{state.results.map((r, i)=>{
          if (!r._ok) return null;
          return (<tr key={i}>
            <td><input type="checkbox" checked={!!keep[i]} onChange={e=>{const k=[...keep]; k[i]=e.target.checked; setKeep(k);}} data-testid={`keep-fs-${i}`}/></td>
            <td><b>{r.venue_name || <span className="muted">(no name)</span>}</b></td>
            <td className="small">{r.brand} {r.sub_brand && `· ${r.sub_brand}`}</td>
            <td className="small">{[r.city, r.state].filter(Boolean).join(", ")}</td>
            <td className="small">{(r.rooms||[]).length}</td>
            <td className="small">{(r.spaces||[]).length}</td>
            <td className="small">{(r.dining||[]).length}</td>
            <td className="muted small truncate" style={{maxWidth:180}}>{r._filename}</td>
          </tr>);
        })}</tbody></table>
      </>)}
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        {ok.length > 0 && <button className="btn-primary" onClick={saveAll} disabled={saving} data-testid="save-bulk-factsheets">
          {saving ? `Saving… ${saved}` : `Save ${keep.filter(Boolean).length} hotels`}
        </button>}
      </div>
    </Modal>
  );
}

function HotelDetail({ hotel, onUpdate }) {
  const { user } = useAuth();
  const [edit, setEdit] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [dining, setDining] = useState([]);
  const [tab, setTab] = useState("overview");
  const load = async () => {
    try {
      const [c, s, r, d] = await Promise.all([
        api.get("/contacts", { params: { hotel_id: hotel.id }}),
        api.get(`/hotels/${hotel.id}/spaces`),
        api.get(`/hotels/${hotel.id}/rooms`),
        api.get(`/hotels/${hotel.id}/dining`),
      ]);
      setContacts(c.data); setSpaces(s.data); setRooms(r.data); setDining(d.data);
    } catch (e) { console.warn(e?.response?.status); }
  };
  useEffect(()=>{ load(); api.post(`/recent/hotels/${hotel.id}`).catch(()=>{}); /* eslint-disable-next-line */ }, [hotel.id]);

  return (
    <div className="detail">
      <div className="detail-h">
        <div>
          <h2>{hotel.venue_name} {hotel.preferred && <Star size={16} className="star"/>}</h2>
          <div className="muted">{[hotel.area, hotel.city, hotel.state, hotel.country].filter(Boolean).join(", ")}{hotel.pin_code?` • ${hotel.pin_code}`:""}</div>
          <div className="tags">
            {(hotel.types||[]).map(t=><span key={t} className="tag dark">{t}</span>)}
            {(hotel.categories||[]).map(t=><span key={t} className="tag dark">{t}</span>)}
            {(hotel.ratings||[]).map(t=><span key={t} className="tag lime">{t}</span>)}
          </div>
        </div>
        <button className="btn-primary" onClick={()=>setEdit(true)} data-testid="edit-hotel"><Edit3 size={14}/> Edit</button>
        {user?.role === "admin" && <button className="btn-danger" style={{marginLeft:6}} onClick={()=>confirmDelete(`hotel "${hotel.venue_name}"`, async()=>{ await api.delete(`/hotels/${hotel.id}`); onUpdate(); })} data-testid="delete-hotel"><Trash2 size={14}/></button>}
      </div>
      <div className="tabs">
        {["overview","rooms","spaces","dining","contacts","quotes","attachments"].map(t=>(
          <button key={t} className={tab===t?"active":""} onClick={()=>setTab(t)} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>
      {tab==="overview" && <HotelOverview hotel={hotel} contacts={contacts} rooms={rooms} spaces={spaces} dining={dining}/>}
      {tab==="rooms" && <Rooms hotelId={hotel.id} rooms={rooms} reload={load}/>}
      {tab==="spaces" && <Spaces hotelId={hotel.id} spaces={spaces} reload={load}/>}
      {tab==="dining" && <Dining hotelId={hotel.id} dining={dining} reload={load}/>}
      {tab==="contacts" && <HotelContacts hotelId={hotel.id} hotelName={hotel.venue_name} contacts={contacts} reload={load}/>}
      {tab==="quotes" && <Quotes parentType="hotel" parentId={hotel.id} parentName={hotel.venue_name} rooms={rooms}/>}
      {tab==="attachments" && <Attachments hotel={hotel} onUpdate={onUpdate}/>}
      {edit && <HotelForm hotel={hotel} onClose={()=>setEdit(false)} onSaved={()=>{ setEdit(false); onUpdate(); }}/>}
    </div>
  );
}

function HotelOverview({ hotel, contacts, rooms, spaces, dining }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [showImp, setShowImp] = useState(false);
  const [panelVendors, setPanelVendors] = useState([]);
  useEffect(()=>{ api.get(`/hotels/${hotel.id}/panel-vendors`).then(r=>setPanelVendors(r.data)).catch(()=>{}); }, [hotel.id]);
  const totalRooms = hotel.no_of_rooms || rooms.reduce((s,r)=>s+(parseInt(r.no_of_rooms)||0), 0);
  return (
    <div className="overview-grid">
      <div className="panel">
        <h3>Basic Info</h3>
        <table className="kv">
          <tbody>
          {hotel.brand && <tr><td>Brand</td><td>{hotel.brand}{hotel.sub_brand && ` • ${hotel.sub_brand}`}</td></tr>}
          {totalRooms > 0 && <tr><td>No. of Rooms</td><td>{totalRooms}</td></tr>}
          {hotel.website && <tr><td>Website</td><td><a href={hotel.website} target="_blank" rel="noreferrer">{hotel.website}</a></td></tr>}
          {hotel.distance_airport && <tr><td>Distance to Airport</td><td>{hotel.distance_airport}</td></tr>}
          {hotel.travel_time_road && <tr><td>Travel Time by Road</td><td>{hotel.travel_time_road}</td></tr>}
          {(hotel.nearest_airport||[]).length>0 && <tr><td>Nearest Airport</td><td>{hotel.nearest_airport.join(", ")}</td></tr>}
          {(hotel.nearest_railway||[]).length>0 && <tr><td>Nearest Railway</td><td>{hotel.nearest_railway.join(", ")}</td></tr>}
          {hotel.pref_season && <tr><td>Preferred Season</td><td>{hotel.pref_season}</td></tr>}
          {hotel.avoid_season && <tr><td>Avoid Season</td><td>{hotel.avoid_season}</td></tr>}
          {hotel.google_location && <tr><td>Google Location</td><td><a href={hotel.google_location} target="_blank" rel="noreferrer">Open Map</a></td></tr>}
          </tbody>
        </table>
        {(hotel.images||[]).length>0 && (
          <div className="img-row">
            {hotel.images.map((im,i)=>(<a key={i} href={`${API.replace(/\/api$/,"")}${im.url}`} target="_blank" rel="noreferrer">
              <img src={`${API.replace(/\/api$/,"")}${im.url}`} alt="" className="thumb"/></a>))}
          </div>
        )}
      </div>
      <div className="panel">
        <h3>Notes</h3>
        {hotel.rooms_note && <div className="note-block"><b>Rooms:</b><pre>{hotel.rooms_note}</pre></div>}
        {hotel.venue_notes && <div className="note-block"><b>Venues:</b><pre>{hotel.venue_notes}</pre></div>}
        {hotel.general_notes && <div className="note-block"><b>General:</b><pre>{hotel.general_notes}</pre></div>}
        {hotel.imp_notes && isAdmin && (
          showImp
            ? <div className="note-block imp"><b>Important <span className="muted small">(admin-only)</span>:</b><pre>{hotel.imp_notes}</pre>
                <button className="btn-ghost sm" onClick={()=>setShowImp(false)} style={{marginTop:4}}>Hide</button></div>
            : <div className="note-block imp"><b>Important <span className="muted small">(admin-only)</span>:</b>
                <button className="btn-ghost sm" onClick={()=>setShowImp(true)} style={{marginLeft:8}} data-testid="reveal-imp-notes">🔒 Click to reveal</button></div>
        )}
        {!hotel.rooms_note && !hotel.venue_notes && !hotel.general_notes && !(hotel.imp_notes && isAdmin) && <div className="muted">No notes yet</div>}
      </div>
      {rooms.length > 0 && <div className="panel full"><h3>Rooms ({rooms.length} categories)</h3>
        <table className="tbl"><thead><tr><th>Type</th><th>No.</th><th>Area</th><th>Note</th></tr></thead>
          <tbody>{rooms.map(r=>(<tr key={r.id}><td>{r.room_type}</td><td>{r.no_of_rooms}</td><td>{r.area}</td><td>{r.note}</td></tr>))}</tbody></table></div>}
      {spaces.length > 0 && <div className="panel full"><h3>Banquet Spaces ({spaces.length})</h3>
        <table className="tbl"><thead><tr><th>Name</th><th>Type</th><th>Area</th><th>L×W×H</th><th>Theater</th><th>Cluster</th><th>Float.</th><th>U-Shape</th><th>Classrm</th><th>Note</th></tr></thead>
          <tbody>{spaces.map(s=>(<tr key={s.id}><td><b>{s.name}</b></td><td>{s.indoor_outdoor}</td><td>{s.area}</td>
            <td className="small">{[s.length,s.width,s.height].filter(Boolean).join("×")||s.dimensions||""}</td>
            <td>{s.cap_theater}</td><td>{s.cap_cluster}</td><td>{s.cap_floating}</td><td>{s.cap_ushape}</td><td>{s.cap_classroom}</td><td className="truncate">{s.note}</td></tr>))}</tbody></table></div>}
      {dining.length > 0 && <div className="panel full"><h3>Dining / Restaurants ({dining.length})</h3>
        <table className="tbl"><thead><tr><th>Name</th><th>Type</th><th>Capacity</th><th>Note</th></tr></thead>
          <tbody>{dining.map(d=>(<tr key={d.id}><td><b>{d.name}</b></td><td>{d.type}</td><td>{d.capacity}</td><td>{d.note}</td></tr>))}</tbody></table></div>}
      {contacts.length > 0 && <div className="panel full"><h3>Contacts ({contacts.length})</h3>
        <div className="contact-grid">
          {contacts.map(c=>(
            <div key={c.id} className="contact-card">
              <div className="cc-h"><b>{c.prefix?c.prefix+" ":""}{c.name}</b>{c.poc && <span className="badge-poc">POC</span>}</div>
              <div className="muted">{c.designation}{c.ref_code?` • ${c.ref_code}`:""}</div>
              {c.work_email && <div><Mail size={12}/> {c.work_email}</div>}
              {c.work_mobile && <div><Phone size={12}/> {c.work_mobile}</div>}
            </div>
          ))}
        </div></div>}
      {panelVendors.length>0 && <div className="panel full"><h3>Panel Vendors ({panelVendors.length})</h3>
        <div className="chip-list">{panelVendors.map(v=><span key={v.id} className="chip">{v.name} <span className="muted">({v.category})</span></span>)}</div></div>}
    </div>
  );
}

function HotelForm({ hotel, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [f, setF] = useState(hotel || { venue_name:"", country:"India", state:"", city:"", area:"", pin_code:"",
    brand:"", sub_brand:"", no_of_rooms:"", google_location:"", distance_airport:"", travel_time_road:"",
    nearest_airport:[], nearest_railway:[],
    pref_season:"", avoid_season:"", website:"", preferred:false,
    categories:[], types:[], ratings:[], rooms_note:"", venue_notes:"", general_notes:"", imp_notes:"",
    images:[] });
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showAdminNotes, setShowAdminNotes] = useState(false);
  const set = (k,v)=>{ setF({...f, [k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  useDraft(hotel?`hotel-${hotel.id}`:"new-hotel", f, setF, !hotel);
  const save = async () => {
    if (!f.venue_name) { toast.error("Hotel name is required"); return; }
    const payload = {...f, no_of_rooms: f.no_of_rooms ? parseInt(f.no_of_rooms):null};
    const r = hotel ? await api.put(`/hotels/${hotel.id}`, payload) : await api.post("/hotels", payload);
    clearDraft(hotel?`hotel-${hotel.id}`:"new-hotel"); setDirty(false);
    toast.success(hotel?"Hotel updated":"Hotel added"); onSaved(r.data);
  };
  const scanFactSheet = async (file) => {
    setScanning(true); setScanProgress("1/3 Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      setScanProgress("2/3 AI extracting fields…");
      const r = await api.post("/ocr/fact-sheet", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanProgress("3/3 Filling form…");
      const d = r.data || {};
      // Smart fill: when editing existing hotel, only fill BLANK fields; when adding new, take AI value
      const isExisting = !!hotel;
      const fill = (cur, ai) => isExisting ? (cur || ai) : (ai || cur);
      const fillArr = (cur, ai) => {
        if (!isExisting) return (ai?.length ? ai : cur);
        return (cur && cur.length) ? cur : (ai || []);
      };
      let filledCount = 0, keptCount = 0;
      const fieldsToCheck = ["venue_name","brand","sub_brand","country","state","city","area","pin_code","website","no_of_rooms","distance_airport","travel_time_road","rooms_note","venue_notes","general_notes","imp_notes"];
      fieldsToCheck.forEach(k => { if (isExisting && f[k] && d[k]) keptCount++; else if (d[k] && !f[k]) filledCount++; });
      setF(p=>({...p,
        venue_name: fill(p.venue_name, d.venue_name),
        brand: fill(p.brand, d.brand), sub_brand: fill(p.sub_brand, d.sub_brand),
        country: fill(p.country, d.country), state: fill(p.state, d.state), city: fill(p.city, d.city),
        area: fill(p.area, d.area), pin_code: fill(p.pin_code, d.pin_code), website: fill(p.website, d.website),
        no_of_rooms: fill(p.no_of_rooms, d.no_of_rooms),
        distance_airport: fill(p.distance_airport, d.distance_airport),
        travel_time_road: fill(p.travel_time_road, d.travel_time_road),
        nearest_airport: fillArr(p.nearest_airport, d.nearest_airport),
        nearest_railway: fillArr(p.nearest_railway, d.nearest_railway),
        categories: fillArr(p.categories, d.categories),
        types: fillArr(p.types, d.types),
        ratings: fillArr(p.ratings, d.ratings),
        rooms_note: fill(p.rooms_note, d.rooms_note),
        venue_notes: fill(p.venue_notes, d.venue_notes),
        general_notes: fill(p.general_notes, d.general_notes),
        imp_notes: fill(p.imp_notes, d.imp_notes),
        _aiRooms: d.rooms || [], _aiSpaces: d.spaces || [], _aiDining: d.dining || [],
      }));
      const ai = (d.rooms?.length||0) + (d.spaces?.length||0) + (d.dining?.length||0);
      const msg = isExisting
        ? `Filled ${filledCount} blank fields, kept ${keptCount} existing.${ai?` ${ai} new rows queued.`:""}`
        : `Fact sheet scanned${ai?` • ${ai} room/space/dining rows queued`:""}`;
      toast.success(msg);
    } catch (e) { toast.error("Could not scan fact sheet"); }
    finally { setScanning(false); setScanProgress(""); setDirty(true); }
  };
  const uploadImage = async (file) => {
    if (!hotel) { toast.error("Save hotel first, then add images"); return; }
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/hotels/${hotel.id}/images`, fd, { headers: { "Content-Type":"multipart/form-data" }});
      set("images", [...(f.images||[]), r.data]);
      toast.success("Image added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  // Save AI-extracted sub-records after hotel save (only on create)
  const saveWithAi = async () => {
    const payload = {...f, no_of_rooms: f.no_of_rooms ? parseInt(f.no_of_rooms):null};
    const aiRooms = f._aiRooms || []; const aiSpaces = f._aiSpaces || []; const aiDining = f._aiDining || [];
    delete payload._aiRooms; delete payload._aiSpaces; delete payload._aiDining;
    const r = hotel ? await api.put(`/hotels/${hotel.id}`, payload) : await api.post("/hotels", payload);
    const hid = r.data.id;
    // If editing existing hotel, skip rows whose name/type already exists to avoid duplicates
    let existingRooms = [], existingSpaces = [], existingDining = [];
    if (hotel) {
      try {
        const [er, es, ed] = await Promise.all([
          api.get(`/hotels/${hid}/rooms`), api.get(`/hotels/${hid}/spaces`), api.get(`/hotels/${hid}/dining`)]);
        existingRooms = (er.data||[]).map(x=>(x.room_type||"").toLowerCase());
        existingSpaces = (es.data||[]).map(x=>(x.name||"").toLowerCase());
        existingDining = (ed.data||[]).map(x=>(x.name||"").toLowerCase());
      } catch {}
    }
    let added = 0, skipped = 0;
    for (const rm of aiRooms) {
      if (!rm.room_type?.trim()) continue;
      if (existingRooms.includes((rm.room_type||"").toLowerCase())) { skipped++; continue; }
      await api.post(`/hotels/${hid}/rooms`, rm).catch(()=>{}); added++;
    }
    for (const sp of aiSpaces) {
      if (!sp.name?.trim()) continue;
      if (existingSpaces.includes((sp.name||"").toLowerCase())) { skipped++; continue; }
      // Normalise unit text → lowercase with dots
      const normUnit = (u) => {
        if (!u) return "";
        const ul = String(u).toLowerCase().replace(/[\s_]+/g,"").replace(/\./g,"");
        if (ul.includes("sqft") || ul === "sf") return "sq.ft";
        if (ul.includes("sqm") || ul.includes("m2") || ul.includes("sqmtr")) return "sq.m";
        if (ul.includes("sqyd")) return "sq.yd";
        return String(u).toLowerCase();
      };
      let payload = {...sp};
      const unit = normUnit(sp.area_unit);
      const conv = sp.area_sqft_converted;
      if (conv && unit && unit !== "sq.ft") {
        payload.area = `${conv} sq.ft (orig: ${sp.area||""} ${unit})`;
      } else if (sp.area && unit) {
        payload.area = `${sp.area} ${unit}`;
      } else if (sp.area) {
        payload.area = String(sp.area);
      } else {
        payload.area = "";  // keep row even without area
      }
      delete payload.area_unit; delete payload.area_sqft_converted;
      await api.post(`/hotels/${hid}/spaces`, payload).catch(()=>{}); added++;
    }
    for (const dn of aiDining) {
      if (!dn.name?.trim()) continue;
      if (existingDining.includes((dn.name||"").toLowerCase())) { skipped++; continue; }
      await api.post(`/hotels/${hid}/dining`, dn).catch(()=>{}); added++;
    }
    toast.success(`Saved • ${added} new rows added${skipped?`, ${skipped} duplicates skipped`:""}`);
    onSaved(r.data);
  };
  return (
    <Modal title={hotel?"Edit Hotel":"Add Hotel"} onClose={onClose} wide dirty={dirty}>
      <div className="scan-bar">
        <label className="btn-secondary"><FileScan size={14}/> {scanning?(scanProgress||"Scanning…"):"Scan Fact Sheet (PDF/Image)"}
          <input type="file" accept="application/pdf,image/*" hidden onChange={e=>e.target.files[0]&&scanFactSheet(e.target.files[0])}/>
        </label>
        <span className="muted small">{hotel ? "Fills only blank fields — your existing data is preserved" : "AI extracts fields from your fact sheet"} • PDF not stored, only extracted text</span>
      </div>
      <div className="form-grid">
        <Field label="Venue Name *"><input value={f.venue_name} onChange={e=>set("venue_name",e.target.value)} data-testid="hotel-name"/></Field>
        <Field label="Country"><Typeahead type="country" value={f.country} onChange={v=>set("country",v)}/></Field>
        <Field label="State"><Typeahead type="state" parent={f.country} value={f.state} onChange={v=>set("state",v)}/></Field>
        <Field label="City"><Typeahead type="city" parent={f.state} value={f.city} onChange={v=>set("city",v)}/></Field>
        <Field label="Area"><Typeahead type="area" parent={f.city} value={f.area} onChange={v=>set("area",v)}/></Field>
        <Field label="Pin Code"><Typeahead type="pin_code" parent={f.city} value={f.pin_code} onChange={v=>set("pin_code",v)}/></Field>
        <Field label="Brand"><Typeahead type="brand" value={f.brand} onChange={v=>set("brand",v)}/></Field>
        <Field label="Sub-Brand"><Typeahead type="sub_brand" parent={f.brand} value={f.sub_brand} onChange={v=>set("sub_brand",v)}/></Field>
        <Field label="No. of Rooms"><input type="number" value={f.no_of_rooms} onChange={e=>set("no_of_rooms",e.target.value)}/></Field>
        <Field label="Distance to Airport"><input value={f.distance_airport} onChange={e=>set("distance_airport",e.target.value)} placeholder="e.g. 15 km"/></Field>
        <Field label="Travel Time by Road"><input value={f.travel_time_road} onChange={e=>set("travel_time_road",e.target.value)} placeholder="e.g. 30 min"/></Field>
        <Field label="Google Maps Link"><input value={f.google_location} onChange={e=>set("google_location",e.target.value)} placeholder="https://maps.google.com/..."/></Field>
        <Field label="Nearest Airport(s)" full><TagInput value={f.nearest_airport||[]} onChange={v=>set("nearest_airport",v)} placeholder="Add airport and press Enter"/></Field>
        <Field label="Nearest Railway Station(s)" full><TagInput value={f.nearest_railway||[]} onChange={v=>set("nearest_railway",v)} placeholder="Add station and press Enter"/></Field>
        <Field label="Preferred Season"><input value={f.pref_season} onChange={e=>set("pref_season",e.target.value)}/></Field>
        <Field label="Avoid Season"><input value={f.avoid_season} onChange={e=>set("avoid_season",e.target.value)}/></Field>
        <Field label="Website"><input value={f.website} onChange={e=>set("website",e.target.value)}/></Field>
        <Field label="Preferred"><label className="chk"><input type="checkbox" checked={!!f.preferred} onChange={e=>set("preferred",e.target.checked)}/> Mark as preferred ⭐</label></Field>
        <Field label="Category" full><MultiSelect type="category" value={f.categories||[]} onChange={v=>set("categories",v)}/></Field>
        <Field label="Type" full><MultiSelect type="type" value={f.types||[]} onChange={v=>set("types",v)}/></Field>
        <Field label="Rating" full><MultiSelect type="rating" value={f.ratings||[]} onChange={v=>set("ratings",v)}/></Field>
        <Field label="Rooms Note" full><textarea rows={3} value={f.rooms_note} onChange={e=>set("rooms_note",e.target.value)}/></Field>
        <Field label="Venue Notes" full><textarea rows={3} value={f.venue_notes} onChange={e=>set("venue_notes",e.target.value)}/></Field>
        <Field label="General Notes" full><textarea rows={3} value={f.general_notes} onChange={e=>set("general_notes",e.target.value)}/></Field>
        {isAdmin && (
          <Field label={
            <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
              Important Notes <span className="tag dark sm" style={{fontSize:9}}>admin-only</span>
              <button type="button" className="btn-ghost sm" style={{padding:"0 6px", fontSize:11}}
                onClick={()=>setShowAdminNotes(!showAdminNotes)} data-testid="toggle-imp-notes">
                {showAdminNotes ? "Hide" : (f.imp_notes ? "Show ●" : "Add")}
              </button>
            </span>
          } full>
            {showAdminNotes
              ? <textarea rows={2} value={f.imp_notes} onChange={e=>set("imp_notes",e.target.value)} data-testid="hotel-imp-notes"
                  placeholder="Confidential notes — visible only to admins"/>
              : <div className="muted small" style={{padding:"6px 10px", border:"1px dashed #d4af37", borderRadius:6, background:"var(--tint)"}}>
                  {f.imp_notes ? "🔒 Important notes are hidden by default. Click 'Show' to reveal." : "No important notes. Click 'Add' to write admin-only notes."}
                </div>}
          </Field>
        )}
        {hotel && <Field label="Hotel Images (max 2, auto-compressed)" full>
          <div className="img-upload">
            {(f.images||[]).map((im,i)=>(<div key={i} className="img-tile">
              <img src={`${API.replace(/\/api$/,"")}${im.url}`} alt=""/>
              <button onClick={async()=>{await api.delete(`/hotels/${hotel.id}/images/${i}`); set("images", f.images.filter((_,x)=>x!==i));}}><Trash2 size={12}/></button></div>))}
            {(f.images||[]).length<2 && <label className="btn-secondary sm"><ImgIcon size={14}/> Add Image
              <input type="file" accept="image/*" hidden onChange={e=>e.target.files[0]&&uploadImage(e.target.files[0])}/></label>}
          </div>
        </Field>}
      </div>
      <div className="modal-actions">
        <button className="btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn-primary" onClick={(f._aiRooms||f._aiSpaces||f._aiDining)?saveWithAi:save} data-testid="save-hotel">Save</button>
      </div>
    </Modal>
  );
}

// Tag input for multi-value text fields
function TagInput({ value=[], onChange, placeholder }) {
  const [q, setQ] = useState("");
  const add = () => { const v = q.trim(); if (v && !value.includes(v)) onChange([...value, v]); setQ(""); };
  return (
    <div className="ms-input">
      {value.map(v => <span key={v} className="chip">{v}<X size={12} onClick={()=>onChange(value.filter(x=>x!==v))}/></span>)}
      <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();add();}}} placeholder={placeholder}/>
    </div>
  );
}

function Rooms({ hotelId, rooms, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Rooms / Categories</h3><button className="btn-primary sm" onClick={()=>setAdd(true)}><Plus size={14}/> Add</button></div>
      <table className="tbl"><thead><tr><th>Room Type</th><th>No. of Rooms</th><th>Area</th><th>Note</th><th></th></tr></thead>
        <tbody>{rooms.map(r=>(<tr key={r.id}><td><b>{r.room_type}</b></td><td>{r.no_of_rooms}</td><td>{r.area}</td><td>{r.note}</td>
          <td><button className="icon-btn" onClick={()=>setEdit(r)}><Edit3 size={14}/></button>
            <button className="icon-btn danger" onClick={async()=>{await api.delete(`/rooms/${r.id}`); reload();}}><Trash2 size={14}/></button></td></tr>))}
        {rooms.length===0 && <tr><td colSpan={5} className="empty-tr">No rooms yet</td></tr>}
        </tbody></table>
      {(add||edit) && <RoomForm hotelId={hotelId} room={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}
function RoomForm({ hotelId, room, onClose, onSaved }) {
  const [f,setF] = useState(room||{room_type:"",no_of_rooms:"",area:"",note:""});
  const set=(k,v)=>setF({...f,[k]:v});
  const save = async () => {
    if (room) await api.put(`/rooms/${room.id}`, f); else await api.post(`/hotels/${hotelId}/rooms`, f);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={room?"Edit Room":"Add Room Category"} onClose={onClose}>
      <div className="form-grid">
        <Field label="Room Type *"><Typeahead type="room_category" value={f.room_type} onChange={v=>set("room_type",v)}/></Field>
        <Field label="No. of Rooms"><input type="number" value={f.no_of_rooms} onChange={e=>set("no_of_rooms",e.target.value)}/></Field>
        <Field label="Area"><input value={f.area} onChange={e=>set("area",e.target.value)} placeholder="e.g. 437 sq m"/></Field>
        <Field label="Note" full><textarea rows={2} value={f.note} onChange={e=>set("note",e.target.value)} placeholder="e.g. queen beds, sea-view"/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save}>Save</button></div>
    </Modal>
  );
}

function Spaces({ hotelId, spaces, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Banquet / Venue Spaces</h3><button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-space"><Plus size={14}/> Add</button></div>
      <table className="tbl">
        <thead><tr><th>Name</th><th>In/Out</th><th>Area</th><th>L×W×H</th><th>Theater</th><th>Cluster</th><th>Float</th><th>U</th><th>Class</th><th>Note</th><th></th></tr></thead>
        <tbody>
        {spaces.map(s=>(
          <tr key={s.id}>
            <td><b>{s.name}</b></td><td>{s.indoor_outdoor}</td><td>{s.area}</td>
            <td className="small">{[s.length,s.width,s.height].filter(Boolean).join("×")||s.dimensions||""}</td>
            <td>{s.cap_theater}</td><td>{s.cap_cluster}</td><td>{s.cap_floating}</td>
            <td>{s.cap_ushape}</td><td>{s.cap_classroom}</td>
            <td className="truncate">{s.note}</td>
            <td><button className="icon-btn" onClick={()=>setEdit(s)}><Edit3 size={14}/></button>
              <button className="icon-btn danger" onClick={async ()=>{await api.delete(`/spaces/${s.id}`); reload();}}><Trash2 size={14}/></button></td>
          </tr>
        ))}
        {spaces.length===0 && <tr><td colSpan={11} className="empty-tr">No spaces yet</td></tr>}
        </tbody>
      </table>
      {(add||edit) && <SpaceForm hotelId={hotelId} space={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}

function SpaceForm({ hotelId, space, onClose, onSaved }) {
  const [f,setF] = useState(space||{name:"",indoor_outdoor:"Indoor",area:"",length:"",width:"",height:"",
    cap_theater:"",cap_cluster:"",cap_floating:"",cap_ushape:"",cap_classroom:"",note:""});
  const set=(k,v)=>setF({...f,[k]:v});
  const save = async () => {
    if (space) await api.put(`/spaces/${space.id}`, f); else await api.post(`/hotels/${hotelId}/spaces`, f);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={space?"Edit Space":"Add Space"} onClose={onClose} wide>
      <div className="form-grid">
        <Field label="Name *"><input value={f.name} onChange={e=>set("name",e.target.value)} data-testid="space-name"/></Field>
        <Field label="Indoor / Outdoor"><select value={f.indoor_outdoor} onChange={e=>set("indoor_outdoor",e.target.value)}>
          <option>Indoor</option><option>Outdoor</option><option>Both</option></select></Field>
        <Field label="Area"><input value={f.area} onChange={e=>set("area",e.target.value)} placeholder="e.g. 5000 sq ft"/></Field>
        <Field label="Length"><input value={f.length} onChange={e=>set("length",e.target.value)} placeholder="ft"/></Field>
        <Field label="Width"><input value={f.width} onChange={e=>set("width",e.target.value)} placeholder="ft"/></Field>
        <Field label="Height"><input value={f.height} onChange={e=>set("height",e.target.value)} placeholder="ft"/></Field>
        <Field label="Theater"><input value={f.cap_theater} onChange={e=>set("cap_theater",e.target.value)}/></Field>
        <Field label="Cluster"><input value={f.cap_cluster} onChange={e=>set("cap_cluster",e.target.value)}/></Field>
        <Field label="Floating"><input value={f.cap_floating} onChange={e=>set("cap_floating",e.target.value)}/></Field>
        <Field label="U-Shape"><input value={f.cap_ushape} onChange={e=>set("cap_ushape",e.target.value)}/></Field>
        <Field label="Classroom"><input value={f.cap_classroom} onChange={e=>set("cap_classroom",e.target.value)}/></Field>
        <Field label="Note" full><textarea rows={2} value={f.note} onChange={e=>set("note",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save}>Save</button></div>
    </Modal>
  );
}

function Dining({ hotelId, dining, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Dining / Restaurants</h3><button className="btn-primary sm" onClick={()=>setAdd(true)}><Plus size={14}/> Add</button></div>
      <table className="tbl"><thead><tr><th>Name</th><th>Type</th><th>Capacity</th><th>Note</th><th></th></tr></thead>
        <tbody>{dining.map(d=>(<tr key={d.id}><td><b>{d.name}</b></td><td>{d.type}</td><td>{d.capacity}</td><td>{d.note}</td>
          <td><button className="icon-btn" onClick={()=>setEdit(d)}><Edit3 size={14}/></button>
          <button className="icon-btn danger" onClick={async()=>{await api.delete(`/dining/${d.id}`); reload();}}><Trash2 size={14}/></button></td></tr>))}
        {dining.length===0 && <tr><td colSpan={5} className="empty-tr">No restaurants yet</td></tr>}
        </tbody></table>
      {(add||edit) && <DiningForm hotelId={hotelId} dining={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}
function DiningForm({ hotelId, dining, onClose, onSaved }) {
  const [f,setF] = useState(dining||{name:"",type:"All-Day Dining",capacity:"",note:""});
  const set=(k,v)=>setF({...f,[k]:v});
  const save = async () => {
    if (dining) await api.put(`/dining/${dining.id}`, f); else await api.post(`/hotels/${hotelId}/dining`, f);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={dining?"Edit Restaurant":"Add Restaurant"} onClose={onClose}>
      <div className="form-grid">
        <Field label="Name *"><input value={f.name} onChange={e=>set("name",e.target.value)}/></Field>
        <Field label="Type"><select value={f.type} onChange={e=>set("type",e.target.value)}>
          <option>All-Day Dining</option><option>Specialty</option><option>Bar / Lounge</option><option>Café</option><option>In-Room Dining</option></select></Field>
        <Field label="Capacity"><input value={f.capacity} onChange={e=>set("capacity",e.target.value)} placeholder="pax"/></Field>
        <Field label="Note" full><textarea rows={2} value={f.note} onChange={e=>set("note",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save}>Save</button></div>
    </Modal>
  );
}

function HotelContacts({ hotelId, hotelName, contacts, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Contacts ({contacts.length})</h3>
        <div className="row-btns">
          <button className="btn-secondary sm" onClick={()=>setAdd("scan")} data-testid="scan-card"><Camera size={14}/> Scan Card</button>
          <button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-contact"><Plus size={14}/> Add</button>
        </div>
      </div>
      <div className="contact-grid">
        {contacts.map(c=>(
          <div key={c.id} className="contact-card" onClick={()=>setEdit(c)}>
            <div className="cc-h">
              <div><b>{c.prefix?c.prefix+" ":""}{c.name}{c.suffix?", "+c.suffix:""}</b>{c.poc && <span className="badge-poc">POC</span>}</div>
              <div style={{display:"flex",gap:4}}>
                <Edit3 size={13} className="muted"/>
                <button className="card-del" data-testid={`del-hcontact-${c.id}`}
                  onClick={async(e)=>{e.stopPropagation(); if(!window.confirm(`Delete contact "${c.name}"?`)) return; await api.delete(`/contacts/${c.id}`); reload();}}>
                  <Trash2 size={12}/></button>
              </div>
            </div>
            <div className="muted">{c.designation}{c.ref_code?` • ${c.ref_code}`:""}</div>
            {c.work_email && <div><Mail size={12}/> {c.work_email}</div>}
            {c.work_mobile && <div><Phone size={12}/> {c.work_mobile}</div>}
            {c.personal_mobile && <div><Phone size={12}/> {c.personal_mobile} <span className="muted">(personal)</span></div>}
            {(c.dob||c.anniversary) && <div className="muted small">{c.dob && <><Calendar size={11}/> DOB {c.dob} </>}{c.anniversary && <>• Anniv {c.anniversary}</>}</div>}
          </div>
        ))}
        {contacts.length===0 && <div className="empty">No contacts yet</div>}
      </div>
      {(add||edit) && <ContactForm hotelId={hotelId} hotelName={hotelName} contact={edit} scanMode={add==="scan"} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}

function ContactForm({ hotelId, hotelName, contact, scanMode, onClose, onSaved }) {
  const [f,setF] = useState(contact||{prefix:"",name:"",suffix:"",designation:"",ref_code:"",poc:false,
    work_email:"",personal_email:"",work_mobile:"",personal_mobile:"",country:"India",
    dob:"",anniversary:"",spouse_name:"",spouse_dob:"",note:"",labels:[],
    vcard_front:"",vcard_back:"",hotel_id:hotelId, hotel_name:hotelName});
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [allHotels, setAllHotels] = useState([]);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  useEffect(()=>{
    if (contact && showMove && allHotels.length === 0) {
      api.get("/hotels").then(r=>setAllHotels(r.data||[])).catch(()=>{});
    }
  }, [showMove, contact, allHotels.length]);
  const moveToHotel = async (targetHotelId) => {
    if (!contact || !targetHotelId) return;
    try {
      const r = await api.post(`/contacts/${contact.id}/move`, { hotel_id: targetHotelId });
      toast.success(`Moved to ${r.data.hotel_name}`);
      setF({...f, hotel_id: r.data.hotel_id, hotel_name: r.data.hotel_name});
      setShowMove(false);
      // Refresh parent
      if (onSaved) onSaved();
    } catch (e) { toast.error("Move failed"); }
  };
  const moveToNewHotel = async (newName) => {
    if (!contact || !newName?.trim()) return;
    try {
      const r = await api.post(`/contacts/${contact.id}/move`, { new_hotel_name: newName.trim() });
      toast.success(`Moved to new hotel: ${r.data.hotel_name}`);
      setF({...f, hotel_id: r.data.hotel_id, hotel_name: r.data.hotel_name});
      setShowMove(false);
      if (onSaved) onSaved();
    } catch (e) { toast.error("Move failed"); }
  };
  const onScan = async (file) => {
    setScanning(true); setScanProgress("1/3 Uploading…");
    try {
      const fd = new FormData(); fd.append("file", file);
      setScanProgress("2/3 AI extracting…");
      const r = await api.post("/ocr/business-card", fd);
      const d = r.data;
      // Duplicate detection: check existing contacts by email/mobile
      try {
        const existing = await api.get("/contacts", { params: { q: d.work_email || d.work_mobile || d.personal_mobile || "" }});
        const dup = (existing.data || []).find(c =>
          (d.work_email && c.work_email && c.work_email.toLowerCase() === d.work_email.toLowerCase()) ||
          (d.work_mobile && (c.work_mobile === d.work_mobile || c.personal_mobile === d.work_mobile))
        );
        if (dup && window.confirm(`Found existing contact "${dup.name}" with same email/mobile. Open that one to update instead?`)) {
          toast.info("Tip: close this and edit the existing contact"); return;
        }
      } catch {}
      setScanProgress("3/3 Filling form…");
      setF(prev=>({...prev,
        name: d.name||prev.name, designation: d.designation||prev.designation,
        work_email: d.work_email||prev.work_email, personal_email: d.personal_email||prev.personal_email,
        work_mobile: d.work_mobile||prev.work_mobile, personal_mobile: d.personal_mobile||prev.personal_mobile,
      })); setDirty(true);
      toast.success("Card scanned — review and save");
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); setScanProgress(""); }
  };
  const uploadVcard = async (file, side) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd); set(side, r.data.url);
  };
  const save = async () => {
    if (contact) await api.put(`/contacts/${contact.id}`, f);
    else await api.post("/contacts", f);
    setDirty(false);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={contact?"Edit Contact":(scanMode?"Scan Business Card":"Add Contact")} onClose={onClose} wide dirty={dirty}>
      {(scanMode || !contact) && (
        <div className="scan-bar">
          <label className="btn-secondary"><Camera size={14}/> {scanning?(scanProgress||"Scanning…"):"Scan Card"}
            <input type="file" accept="image/*" capture="environment" hidden onChange={e=>e.target.files[0]&&onScan(e.target.files[0])}/>
          </label>
          <span className="muted">Fields auto-fill from scan</span>
        </div>
      )}
      {contact && (
        <div className="panel" style={{marginBottom:10, padding:"8px 10px", background:"var(--tint)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap"}}>
            <div className="small">
              <b>Hotel:</b> {f.hotel_name || <span className="muted">(none)</span>}
            </div>
            <button type="button" className="btn-ghost sm" onClick={()=>setShowMove(!showMove)} data-testid="toggle-move-hotel">
              {showMove ? "Cancel move" : "📍 Move to another hotel…"}
            </button>
          </div>
          {showMove && (
            <div style={{marginTop:8, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
              <SearchableSelect
                items={allHotels.filter(h=>h.id!==f.hotel_id).map(h=>({id:h.id, label:h.venue_name}))}
                value=""
                onChange={(id)=>moveToHotel(id)}
                placeholder="Type to search hotels…"
                testid="move-hotel-search"/>
              <span className="muted small">or</span>
              <input className="ipt" placeholder="+ Create new hotel name" style={{flex:1, minWidth:200}}
                onKeyDown={e=>{ if(e.key==="Enter" && e.target.value.trim()){ moveToNewHotel(e.target.value); e.target.value=""; }}}
                data-testid="move-hotel-new"/>
            </div>
          )}
        </div>
      )}
      <div className="form-grid">
        <Field label="Prefix"><Typeahead type="prefix" value={f.prefix} onChange={v=>set("prefix",v)}/></Field>
        <Field label="Name *"><input value={f.name} onChange={e=>set("name",e.target.value)} data-testid="contact-name"/></Field>
        <Field label="Suffix"><input value={f.suffix} onChange={e=>set("suffix",e.target.value)}/></Field>
        <Field label="Designation"><Typeahead type="designation" value={f.designation} onChange={v=>set("designation",v)}/></Field>
        <Field label="Ref Code"><input value={f.ref_code} onChange={e=>set("ref_code",e.target.value)} placeholder="e.g. NSO, BDM"/></Field>
        <Field label="POC?"><label className="chk"><input type="checkbox" checked={!!f.poc} onChange={e=>set("poc",e.target.checked)}/> Primary contact</label></Field>
        <Field label="Work Email"><input type="email" value={f.work_email} onChange={e=>set("work_email",e.target.value)}/></Field>
        <Field label="Personal Email"><input type="email" value={f.personal_email} onChange={e=>set("personal_email",e.target.value)}/></Field>
        <Field label="Work Mobile"><input value={f.work_mobile} onChange={e=>set("work_mobile",e.target.value)} placeholder="+91 …"/></Field>
        <Field label="Personal Mobile"><input value={f.personal_mobile} onChange={e=>set("personal_mobile",e.target.value)} placeholder="+91 …"/></Field>
        <Field label="Country"><Typeahead type="country" value={f.country} onChange={v=>set("country",v)}/></Field>
        <Field label="DOB"><input type="date" value={f.dob} onChange={e=>set("dob",e.target.value)}/></Field>
        <Field label="Anniversary"><input type="date" value={f.anniversary} onChange={e=>set("anniversary",e.target.value)}/></Field>
        <Field label="Spouse Name"><input value={f.spouse_name} onChange={e=>set("spouse_name",e.target.value)}/></Field>
        <Field label="Spouse DOB"><input type="date" value={f.spouse_dob} onChange={e=>set("spouse_dob",e.target.value)}/></Field>
        <Field label="VCard Front"><input type="file" accept="image/*" onChange={e=>e.target.files[0]&&uploadVcard(e.target.files[0],"vcard_front")}/>
          {f.vcard_front && <a href={`${API.replace(/\/api$/,"")}${f.vcard_front}`} target="_blank" rel="noreferrer">View</a>}</Field>
        <Field label="VCard Back"><input type="file" accept="image/*" onChange={e=>e.target.files[0]&&uploadVcard(e.target.files[0],"vcard_back")}/>
          {f.vcard_back && <a href={`${API.replace(/\/api$/,"")}${f.vcard_back}`} target="_blank" rel="noreferrer">View</a>}</Field>
        <Field label="Note" full><textarea rows={2} value={f.note} onChange={e=>set("note",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-contact">Save</button></div>
    </Modal>
  );
}

function Attachments({ hotel, onUpdate }) {
  const [files, setFiles] = useState(hotel.attachments||[]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd);
    const next = [...files, { url: r.data.url, name: r.data.filename, uploaded: new Date().toISOString() }];
    setFiles(next);
    await api.put(`/hotels/${hotel.id}`, { attachments: next }); onUpdate();
  };
  const del = async (i) => {
    const next = files.filter((_,idx)=>idx!==i);
    setFiles(next); await api.put(`/hotels/${hotel.id}`, { attachments: next }); onUpdate();
  };
  return (
    <div className="panel">
      <div className="panel-h"><h3>Attachments (Fact Sheets, PPT, etc.)</h3>
        <label className="btn-primary sm"><Upload size={14}/> Upload<input type="file" hidden onChange={e=>e.target.files[0]&&upload(e.target.files[0])}/></label></div>
      <div className="file-list">
        {files.map((f,i)=>(<div key={i} className="file-row">
          <a href={`${API.replace(/\/api$/,"")}${f.url}`} target="_blank" rel="noreferrer">{f.name}</a>
          <span className="muted small">{f.uploaded?.substring(0,10)}</span>
          <button className="icon-btn danger" onClick={()=>del(i)}><Trash2 size={14}/></button></div>))}
        {files.length===0 && <div className="empty">No attachments yet</div>}
      </div>
    </div>
  );
}

// ===== CONTACTS standalone =====
function ContactsPage() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q:"", country:"", city:"", brand:"", designation:"", poc:"" });
  const [edit, setEdit] = useState(null);
  const [add, setAdd] = useState(false);
  const load = async () => {
    try {
      const p = {}; Object.entries(filters).forEach(([k,v])=>{ if(v!=="") p[k]= k==="poc" ? v==="yes" : v; });
      const r = await api.get("/contacts", { params: p });
      setRows(r.data);
    } catch {}
  };
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="three-col">
      <aside className="sidebar">
        <div className="side-h"><Filter size={14}/> Filters</div>
        <input className="ipt" placeholder="Search name, email, mobile, designation…" value={filters.q} onChange={e=>setFilters({...filters,q:e.target.value})} onKeyDown={e=>e.key==="Enter"&&load()} data-testid="contacts-search"/>
        <Typeahead type="country" value={filters.country} onChange={v=>setFilters({...filters,country:v})} placeholder="Country"/>
        <Typeahead type="city" value={filters.city} onChange={v=>setFilters({...filters,city:v})} placeholder="City"/>
        <Typeahead type="brand" value={filters.brand} onChange={v=>setFilters({...filters,brand:v})} placeholder="Brand"/>
        <Typeahead type="designation" value={filters.designation} onChange={v=>setFilters({...filters,designation:v})} placeholder="Designation"/>
        <select className="ipt" value={filters.poc} onChange={e=>setFilters({...filters,poc:e.target.value})}>
          <option value="">All</option><option value="yes">POC only</option><option value="no">Non-POC</option>
        </select>
        <div className="row2">
          <button className="btn-primary" onClick={load}><Filter size={14}/> Apply</button>
          <button className="btn-danger" onClick={()=>{setFilters({q:"",country:"",city:"",brand:"",designation:"",poc:""}); setTimeout(load,0);}}><Eraser size={14}/></button>
        </div>
        <div className="results-h"><span>Results ({rows.length})</span>
          <button className="btn-primary sm" onClick={()=>setAdd(true)}><Plus size={14}/> Add</button></div>
        <div className="cards">
          {rows.map(c=>(
            <div key={c.id} className="card" onClick={()=>setEdit(c)}>
              <div className="card-title">{c.prefix?c.prefix+" ":""}{c.name} {c.poc && <span className="badge-poc">POC</span>}
                <button className="card-del" data-testid={`del-contact-${c.id}`}
                  onClick={async(e)=>{e.stopPropagation(); if(!window.confirm(`Delete contact "${c.name}"?`)) return; await api.delete(`/contacts/${c.id}`); load();}}>
                  <Trash2 size={12}/></button></div>
              <div className="card-sub">{c.designation}{c.ref_code?` • ${c.ref_code}`:""}</div>
              <div className="card-sub">{c.hotel_name}</div>
              <div className="card-sub">{c.work_mobile||c.personal_mobile}</div>
              <div className="card-foot">Updated {timeAgo(c.updated_at)}</div>
            </div>
          ))}
        </div>
      </aside>
      <section className="center">
        {rows.length === 0 ? (
          <div className="empty">No contacts found. Use filters on the left or click "+ Add".</div>
        ) : (
          <div className="panel">
            <div className="panel-h">
              <h3>All Contacts ({rows.length})</h3>
              <span className="muted small">Click a row to edit • Names auto-sorted alphabetically</span>
            </div>
            <table className="tbl" data-testid="contacts-table">
              <thead><tr>
                <th>Name</th><th>Designation</th><th>Hotel</th><th>City</th>
                <th>Mobile</th><th>Email</th><th>POC</th><th>Updated</th><th></th>
              </tr></thead>
              <tbody>
                {[...rows].sort((a,b)=>(a.name||"").localeCompare(b.name||"")).map(c=>(
                  <tr key={c.id} className="row-clickable" onClick={()=>setEdit(c)}>
                    <td><b>{c.prefix?c.prefix+" ":""}{c.name}</b>{c.ref_code && <span className="muted small"> • {c.ref_code}</span>}</td>
                    <td>{c.designation}</td>
                    <td>{c.hotel_name}</td>
                    <td>{c.city || ""}</td>
                    <td className="small">{c.work_mobile||c.personal_mobile}</td>
                    <td className="small truncate" style={{maxWidth:220}}>{c.work_email||c.personal_email}</td>
                    <td>{c.poc && <span className="badge-poc">POC</span>}</td>
                    <td className="muted small">{timeAgo(c.updated_at)}</td>
                    <td>
                      <button className="icon-btn danger" data-testid={`tbl-del-contact-${c.id}`}
                        onClick={async(e)=>{e.stopPropagation(); if(!window.confirm(`Delete contact "${c.name}"?`)) return; await api.delete(`/contacts/${c.id}`); load();}}>
                        <Trash2 size={13}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(edit||add) && <ContactForm hotelId={edit?.hotel_id} hotelName={edit?.hotel_name} contact={edit} onClose={()=>{setEdit(null);setAdd(false);}} onSaved={()=>{setEdit(null);setAdd(false);load();}}/>}
      </section>
    </div>
  );
}

// ===== VENDORS =====
function Vendors() {
  const [rows, setRows] = useState([]); const [sel, setSel] = useState(null);
  const [filters, setFilters] = useState({ q:"", category:"", sub_category:"", service_type:"", city:"" });
  const [add, setAdd] = useState(false);
  const load = async () => {
    try {
      const p = Object.fromEntries(Object.entries(filters).filter(([k,v])=>v));
      const r = await api.get("/vendors", { params: p });
      setRows(r.data);
      if (r.data.length && !sel) setSel(r.data[0]);
      if (sel && !r.data.find(x=>x.id===sel.id)) setSel(r.data[0]||null);
    } catch {}
  };
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="three-col">
      <aside className="sidebar">
        <div className="side-h"><Filter size={14}/> Vendors</div>
        <input className="ipt" placeholder="Search name / company / notes…" value={filters.q} onChange={e=>setFilters({...filters,q:e.target.value})} onKeyDown={e=>e.key==="Enter"&&load()} data-testid="vendor-search"/>
        <Typeahead type="vendor_category" value={filters.category} onChange={v=>setFilters({...filters,category:v, sub_category:""})} placeholder="Category"/>
        <Typeahead type="vendor_sub_category" parent={filters.category} value={filters.sub_category} onChange={v=>setFilters({...filters,sub_category:v})} placeholder="Sub-Category"/>
        <Typeahead type="service_type" value={filters.service_type} onChange={v=>setFilters({...filters,service_type:v})} placeholder="Service (Wedding/Corp…)"/>
        <Typeahead type="city" value={filters.city} onChange={v=>setFilters({...filters,city:v})} placeholder="City"/>
        <div className="row2">
          <button className="btn-primary" onClick={load}><Filter size={14}/> Apply</button>
          <button className="btn-danger" onClick={()=>{setFilters({q:"",category:"",sub_category:"",service_type:"",city:""}); setTimeout(load,0);}}><Eraser size={14}/></button>
        </div>
        <div className="results-h"><span>Results ({rows.length})</span>
          <button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-vendor"><Plus size={14}/> Add</button></div>
        <RecentlyViewed kind="vendors" onPick={(v)=>setSel(v)}/>
        <div className="cards">
          {rows.map(v=>(
            <div key={v.id} className={"card "+(sel?.id===v.id?"sel":"")} onClick={()=>setSel(v)}>
              <div className="card-title">{v.name}
                <button className="card-del" data-testid={`del-vendor-${v.id}`}
                  onClick={async(e)=>{e.stopPropagation(); if(!window.confirm(`Delete vendor "${v.name}"?`)) return; await api.delete(`/vendors/${v.id}`); load();}}>
                  <Trash2 size={12}/></button></div>
              <div className="card-sub">{v.category}{v.sub_category?" • "+v.sub_category:""}</div>
              <div className="card-sub">{v.city}{v.coverage_pan_india?" • Pan India":""}</div>
              <div className="tags">{(v.service_types||[]).map(s=><span key={s} className="tag dark sm">{s}</span>)}</div>
              <div className="card-foot">Updated {timeAgo(v.updated_at)}</div>
            </div>
          ))}
        </div>
      </aside>
      <section className="center">
        {sel ? <VendorDetail vendor={sel} reload={load}/> : <div className="empty">Select or add a vendor</div>}
      </section>
      {add && <VendorForm onClose={()=>setAdd(false)} onSaved={(v)=>{setAdd(false);load();setSel(v);}}/>}
    </div>
  );
}

function VendorDetail({ vendor, reload }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview"); const [edit, setEdit] = useState(false);
  const [contacts, setContacts] = useState([]); const [inv, setInv] = useState([]);
  const load = async () => { try {
    const [c,i] = await Promise.all([api.get(`/vendors/${vendor.id}/contacts`), api.get(`/vendors/${vendor.id}/inventory`)]);
    setContacts(c.data); setInv(i.data);
  } catch {} };
  useEffect(()=>{ load(); api.post(`/recent/vendors/${vendor.id}`).catch(()=>{}); /* eslint-disable-next-line */ }, [vendor.id]);
  return (
    <div className="detail">
      <div className="detail-h">
        <div><h2>{vendor.name}</h2>
          <div className="muted">{vendor.company_name}{vendor.company_name?" • ":""}{vendor.category}{vendor.sub_category?" • "+vendor.sub_category:""}</div>
          <div className="muted">{[vendor.city, vendor.state, vendor.country].filter(Boolean).join(", ")}</div>
          <div className="tags">{(vendor.service_types||[]).map(s=><span key={s} className="tag dark">{s}</span>)}{vendor.coverage_pan_india && <span className="tag lime">Pan India</span>}</div>
        </div>
        <button className="btn-primary" onClick={()=>setEdit(true)}><Edit3 size={14}/> Edit</button>
      </div>
      <div className="tabs">{(vendor.is_individual ? ["overview","inventory","quotes"] : ["overview","contacts","inventory","quotes"]).map(t=>(
        <button key={t} className={tab===t?"active":""} onClick={()=>setTab(t)} data-testid={`vendor-tab-${t}`}>{t==="inventory"?"Inventory / Roster":t}</button>))}</div>
      {tab==="overview" && <VendorOverview vendor={vendor} contacts={contacts} inv={inv}/>}
      {tab==="contacts" && <VendorContacts vendorId={vendor.id} contacts={contacts} reload={load}/>}
      {tab==="inventory" && <Inventory vendorId={vendor.id} vendor={vendor} items={inv} reload={load}/>}
      {tab==="quotes" && <Quotes parentType="vendor" parentId={vendor.id} parentName={vendor.name}/>}
      {edit && <VendorForm vendor={vendor} onClose={()=>setEdit(false)} onSaved={()=>{setEdit(false);reload();}}/>}
    </div>
  );
}
function VendorOverview({ vendor, contacts, inv }) {
  const [panelHotels, setPanelHotels] = useState([]);
  useEffect(()=>{
    if (vendor.panel_hotels?.length) {
      Promise.all(vendor.panel_hotels.map(id=>api.get(`/hotels/${id}`).catch(()=>null)))
        .then(rs=>setPanelHotels(rs.filter(Boolean).map(r=>r.data)));
    }
  }, [vendor.id, vendor.panel_hotels]);
  return (
    <div className="overview-grid">
      <div className="panel">
        <h3>{vendor.is_individual ? "Personal Info" : "Basic Info"}</h3>
        <table className="kv"><tbody>
          {vendor.is_individual && vendor.prefix && <tr><td>Prefix</td><td>{vendor.prefix}</td></tr>}
          {vendor.is_individual && vendor.suffix && <tr><td>Suffix</td><td>{vendor.suffix}</td></tr>}
          {vendor.company_name && <tr><td>Company</td><td>{vendor.company_name}</td></tr>}
          {vendor.team_coordinator && <tr><td>Team Coordinator</td><td>{vendor.team_coordinator}</td></tr>}
          {vendor.is_individual && vendor.dob && <tr><td>DOB</td><td>{vendor.dob}</td></tr>}
          {vendor.is_individual && vendor.anniversary && <tr><td>Anniversary</td><td>{vendor.anniversary}</td></tr>}
          {vendor.is_individual && vendor.spouse_name && <tr><td>Spouse</td><td>{vendor.spouse_name}</td></tr>}
          {vendor.website && <tr><td>Website</td><td><a href={vendor.website} target="_blank" rel="noreferrer">{vendor.website}</a></td></tr>}
          {vendor.instagram && <tr><td>Instagram</td><td>{vendor.instagram}</td></tr>}
          {vendor.youtube && <tr><td>YouTube</td><td><a href={vendor.youtube} target="_blank" rel="noreferrer">{vendor.youtube}</a></td></tr>}
          {vendor.facebook && <tr><td>Facebook</td><td><a href={vendor.facebook} target="_blank" rel="noreferrer">{vendor.facebook}</a></td></tr>}
          {vendor.linkedin && <tr><td>LinkedIn</td><td><a href={vendor.linkedin} target="_blank" rel="noreferrer">{vendor.linkedin}</a></td></tr>}
          {vendor.email && <tr><td>{vendor.is_individual?"Work Email":"Email"}</td><td>{vendor.email}</td></tr>}
          {vendor.is_individual && vendor.personal_email && <tr><td>Personal Email</td><td>{vendor.personal_email}</td></tr>}
          {vendor.mobile && <tr><td>{vendor.is_individual?"Work Mobile":"Mobile"}</td><td>{vendor.mobile}</td></tr>}
          {vendor.is_individual && vendor.personal_mobile && <tr><td>Personal Mobile</td><td>{vendor.personal_mobile}</td></tr>}
          {vendor.address && <tr><td>Address</td><td><pre>{vendor.address}</pre></td></tr>}
          {(vendor.coverage||[]).length>0 && <tr><td>Coverage Cities</td><td>{vendor.coverage.join(", ")}</td></tr>}
        </tbody></table>
      </div>
      <div className="panel">
        <h3>Notes</h3>
        {vendor.notes ? <pre>{vendor.notes}</pre> : <div className="muted">No notes</div>}
      </div>
      {vendor.technicals && <div className="panel"><h3>🎚️ Technicals</h3><pre>{vendor.technicals}</pre></div>}
      {contacts.length > 0 && <div className="panel full"><h3>Contacts ({contacts.length})</h3>
        <div className="contact-grid">{contacts.map(c=>(
          <div key={c.id} className="contact-card">
            <div className="cc-h"><b>{c.prefix?c.prefix+" ":""}{c.name}</b>{c.poc && <span className="badge-poc">POC</span>}</div>
            <div className="muted">{c.designation}</div>
            {c.work_email && <div><Mail size={12}/> {c.work_email}</div>}
            {c.work_mobile && <div><Phone size={12}/> {c.work_mobile}</div>}
          </div>))}</div></div>}
      {inv.length > 0 && <div className="panel full"><h3>Inventory ({inv.length} items)</h3>
        <table className="tbl"><thead><tr><th>Item</th><th>Unit</th><th>Rate(C)</th><th>CTC rate</th><th>Profit / unit</th></tr></thead>
          <tbody>{inv.map(i=>{
            const rateC = parseFloat(i.rate_c ?? i.sell_price ?? 0)||0;
            const ctc = parseFloat(i.ctc_rate ?? i.b2b_price ?? i.price ?? 0)||0;
            const profit = rateC - ctc;
            return (<tr key={i.id}><td><b>{i.item}</b></td><td>{i.unit}</td>
              <td>{rateC?`₹${rateC.toLocaleString()}`:""}</td>
              <td>{ctc?`₹${ctc.toLocaleString()}`:""}</td>
              <td>{rateC && ctc ? `${profit>=0?"+":""}₹${profit.toLocaleString()}`:""}</td></tr>);
          })}</tbody></table></div>}
      {panelHotels.length>0 && <div className="panel full"><h3>On Panel With ({panelHotels.length})</h3>
        <div className="chip-list">{panelHotels.map(h=><span key={h.id} className="chip">{h.venue_name}</span>)}</div></div>}
    </div>
  );
}

function VendorForm({ vendor, onClose, onSaved }) {
  const PERFORMER_CATS = ["Singer","DJ","Band","Performer","Artist","Anchor","MC"];
  const [f,setF] = useState(vendor||{name:"",company_name:"",category:"",sub_category:"",
    service_types:[], coverage:[], coverage_pan_india:false, panel_hotels:[],
    is_individual:false, technicals:"",
    team_coordinator:"", mobile:"", email:"", website:"", instagram:"", youtube:"", facebook:"", linkedin:"",
    // Individual-only bio fields
    prefix:"", suffix:"", dob:"", anniversary:"", spouse_name:"", personal_mobile:"", personal_email:"",
    city:"", state:"", country:"India", address:"", notes:""});
  const [dirty, setDirty] = useState(false);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  const [hotels, setHotels] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(!!vendor);
  const isPerformer = PERFORMER_CATS.some(c => (f.category||"").toLowerCase().includes(c.toLowerCase()));
  useEffect(()=>{ api.get("/hotels").then(r=>setHotels(r.data)).catch(()=>{}); }, []);
  const save = async () => {
    if (!f.name) { toast.error("Vendor name is required"); return; }
    const r = vendor ? await api.put(`/vendors/${vendor.id}`, f) : await api.post("/vendors", f);
    setDirty(false);
    toast.success("Saved"); onSaved(r.data);
  };
  return (
    <Modal title={vendor?"Edit Vendor":"Add Vendor"} onClose={onClose} wide dirty={dirty}>
      <div className="form-grid compact">
        <Field label="This is an individual (not a company)" full>
          <label className="chk"><input type="checkbox" checked={!!f.is_individual} onChange={e=>set("is_individual",e.target.checked)}/> Individual — vendor name will also be used as contact name (e.g. solo anchor "Aditya")</label>
        </Field>
        {f.is_individual && <Field label="Prefix"><Typeahead type="prefix" value={f.prefix||""} onChange={v=>set("prefix",v)}/></Field>}
        <Field label={f.is_individual?"Name *":"Vendor Name *"}><input value={f.name} onChange={e=>set("name",e.target.value)} data-testid="vendor-name"/></Field>
        {f.is_individual && <Field label="Suffix"><Typeahead type="suffix" value={f.suffix||""} onChange={v=>set("suffix",v)}/></Field>}
        {!f.is_individual && <Field label="Company Name"><input value={f.company_name} onChange={e=>set("company_name",e.target.value)}/></Field>}
        <Field label="Category"><Typeahead type="vendor_category" value={f.category} onChange={v=>set("category",v)}/></Field>
        <Field label="Sub-Category"><Typeahead type="vendor_sub_category" parent={f.category} value={f.sub_category} onChange={v=>set("sub_category",v)}/></Field>
        <Field label="Service Types" full><MultiSelect type="service_type" value={f.service_types||[]} onChange={v=>set("service_types",v)}/></Field>
        <Field label={f.is_individual?"Mobile (Work)":"Mobile"}><input value={f.mobile} onChange={e=>set("mobile",e.target.value)}/></Field>
        <Field label={f.is_individual?"Email (Work)":"Email"}><input value={f.email} onChange={e=>set("email",e.target.value)}/></Field>
        {f.is_individual && <>
          <Field label="Personal Mobile"><input value={f.personal_mobile||""} onChange={e=>set("personal_mobile",e.target.value)}/></Field>
          <Field label="Personal Email"><input value={f.personal_email||""} onChange={e=>set("personal_email",e.target.value)}/></Field>
          <Field label="DOB"><input type="date" value={f.dob||""} onChange={e=>set("dob",e.target.value)}/></Field>
          <Field label="Anniversary"><input type="date" value={f.anniversary||""} onChange={e=>set("anniversary",e.target.value)}/></Field>
          <Field label="Spouse"><input value={f.spouse_name||""} onChange={e=>set("spouse_name",e.target.value)}/></Field>
        </>}
        <Field label="City"><Typeahead type="city" parent={f.state} value={f.city} onChange={v=>set("city",v)}/></Field>
        <Field label="Pan India?"><label className="chk"><input type="checkbox" checked={!!f.coverage_pan_india} onChange={e=>set("coverage_pan_india",e.target.checked)}/> Works pan-India</label></Field>
      </div>
      <button className="btn-ghost sm" style={{marginTop:8}} onClick={()=>setShowAdvanced(!showAdvanced)} data-testid="vendor-advanced-toggle">
        {showAdvanced?"▾ Hide":"▸ Show"} advanced fields (team, social, panel hotels, address, notes…)
      </button>
      {showAdvanced && (
        <div className="form-grid compact" style={{marginTop:8}}>
          <Field label="Team Coordinator"><input value={f.team_coordinator} onChange={e=>set("team_coordinator",e.target.value)}/></Field>
          <Field label="Website"><input value={f.website} onChange={e=>set("website",e.target.value)}/></Field>
          <Field label="Instagram"><input value={f.instagram} onChange={e=>set("instagram",e.target.value)} placeholder="@handle"/></Field>
          <Field label="YouTube"><input value={f.youtube||""} onChange={e=>set("youtube",e.target.value)} placeholder="channel URL"/></Field>
          <Field label="Facebook"><input value={f.facebook||""} onChange={e=>set("facebook",e.target.value)} placeholder="page URL"/></Field>
          <Field label="LinkedIn"><input value={f.linkedin||""} onChange={e=>set("linkedin",e.target.value)} placeholder="profile URL"/></Field>
          <Field label="Country"><Typeahead type="country" value={f.country} onChange={v=>set("country",v)}/></Field>
          <Field label="State"><Typeahead type="state" parent={f.country} value={f.state} onChange={v=>set("state",v)}/></Field>
          <Field label="Coverage Cities" full><MultiSelect type="city" value={f.coverage||[]} onChange={v=>set("coverage",v)}/></Field>
          <Field label="On Panel with Hotels" full>
            <div className="ms-input" onClick={(e)=>e.stopPropagation()}>
              {(f.panel_hotels||[]).map(id=>{
                const h = hotels.find(x=>x.id===id);
                return <span key={id} className="chip">{h?.venue_name||id}<X size={12} onClick={()=>set("panel_hotels", f.panel_hotels.filter(x=>x!==id))}/></span>;
              })}
              <SearchableSelect
                items={hotels.filter(h=>!(f.panel_hotels||[]).includes(h.id)).map(h=>({id:h.id, label:h.venue_name}))}
                value=""
                onChange={(id)=>{ if(id) set("panel_hotels", [...(f.panel_hotels||[]), id]); }}
                placeholder="+ Add hotel…"/>
            </div>
          </Field>
          <Field label="Address" full><textarea rows={2} value={f.address} onChange={e=>set("address",e.target.value)}/></Field>
          <Field label="Notes" full><textarea rows={2} value={f.notes} onChange={e=>set("notes",e.target.value)}/></Field>
          {isPerformer && <Field label="Technical Rider / Tech Notes" full>
            <textarea rows={4} value={f.technicals||""} onChange={e=>set("technicals",e.target.value)}
              placeholder="Sound, lighting, stage size, AV equipment, monitors, mic count, special requirements…"/>
          </Field>}
        </div>
      )}
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-vendor">Save</button></div>
    </Modal>
  );
}

function VendorContacts({ vendorId, contacts, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Contacts ({contacts.length})</h3>
        <div className="row-btns">
          <button className="btn-secondary sm" onClick={()=>setAdd("scan")}><Camera size={14}/> Scan</button>
          <button className="btn-primary sm" onClick={()=>setAdd(true)}><Plus size={14}/> Add</button>
        </div>
      </div>
      <div className="contact-grid">
        {contacts.map(c=>(
          <div key={c.id} className="contact-card" onClick={()=>setEdit(c)}>
            <div className="cc-h"><b>{c.prefix?c.prefix+" ":""}{c.name}</b>{c.poc&&<span className="badge-poc">POC</span>}
              <button className="card-del" data-testid={`del-vcontact-${c.id}`}
                onClick={async(e)=>{e.stopPropagation(); if(!window.confirm(`Delete contact "${c.name}"?`)) return; await api.delete(`/vendor-contacts/${c.id}`); reload();}}>
                <Trash2 size={12}/></button></div>
            <div className="muted">{c.designation}{c.ref_code?` • ${c.ref_code}`:""}</div>
            {c.work_email && <div><Mail size={12}/> {c.work_email}</div>}
            {c.work_mobile && <div><Phone size={12}/> {c.work_mobile}</div>}
          </div>
        ))}
        {contacts.length===0 && <div className="empty">No contacts yet</div>}
      </div>
      {(add||edit) && <VendorContactForm vendorId={vendorId} contact={edit} scanMode={add==="scan"} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}
function VendorContactForm({ vendorId, contact, scanMode, onClose, onSaved }) {
  const [f,setF] = useState(contact||{prefix:"",name:"",suffix:"",designation:"",ref_code:"",poc:false,
    work_email:"",personal_email:"",work_mobile:"",personal_mobile:"",country:"India",
    dob:"",anniversary:"",spouse_name:"",note:""});
  const [dirty, setDirty] = useState(false);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  const onScan = async (file) => {
    const fd=new FormData(); fd.append("file",file);
    try {
      const r = await api.post("/ocr/business-card", fd); const d=r.data;
      setF(p=>({...p, name:d.name||p.name, suffix:d.suffix||p.suffix, designation:d.designation||p.designation,
        work_email:d.work_email||p.work_email, work_mobile:d.work_mobile||p.work_mobile,
        personal_mobile:d.personal_mobile||p.personal_mobile, personal_email:d.personal_email||p.personal_email}));
      setDirty(true);
      toast.success("Scanned");
    } catch { toast.error("Scan failed"); }
  };
  const save = async () => {
    if (contact) await api.put(`/vendor-contacts/${contact.id}`, f);
    else await api.post(`/vendors/${vendorId}/contacts`, f);
    setDirty(false);
    onSaved();
  };
  return (
    <Modal title={contact?"Edit Contact":(scanMode?"Scan Card":"Add Contact")} onClose={onClose} wide dirty={dirty}>
      {(scanMode||!contact) && <div className="scan-bar">
        <label className="btn-secondary"><Camera size={14}/> Scan Card<input type="file" accept="image/*" capture="environment" hidden onChange={e=>e.target.files[0]&&onScan(e.target.files[0])}/></label>
      </div>}
      <div className="form-grid">
        <Field label="Prefix"><Typeahead type="prefix" value={f.prefix} onChange={v=>set("prefix",v)}/></Field>
        <Field label="Name *"><input value={f.name} onChange={e=>set("name",e.target.value)}/></Field>
        <Field label="Suffix"><Typeahead type="suffix" value={f.suffix||""} onChange={v=>set("suffix",v)}/></Field>
        <Field label="Designation"><Typeahead type="designation" value={f.designation} onChange={v=>set("designation",v)}/></Field>
        <Field label="Ref Code"><input value={f.ref_code} onChange={e=>set("ref_code",e.target.value)}/></Field>
        <Field label="POC?"><label className="chk"><input type="checkbox" checked={!!f.poc} onChange={e=>set("poc",e.target.checked)}/> Primary</label></Field>
        <Field label="Work Email"><input value={f.work_email} onChange={e=>set("work_email",e.target.value)}/></Field>
        <Field label="Personal Email"><input value={f.personal_email} onChange={e=>set("personal_email",e.target.value)}/></Field>
        <Field label="Work Mobile"><input value={f.work_mobile} onChange={e=>set("work_mobile",e.target.value)}/></Field>
        <Field label="Personal Mobile"><input value={f.personal_mobile} onChange={e=>set("personal_mobile",e.target.value)}/></Field>
        <Field label="Country"><Typeahead type="country" value={f.country} onChange={v=>set("country",v)}/></Field>
        <Field label="DOB"><input type="date" value={f.dob} onChange={e=>set("dob",e.target.value)}/></Field>
        <Field label="Anniversary"><input type="date" value={f.anniversary} onChange={e=>set("anniversary",e.target.value)}/></Field>
        <Field label="Spouse"><input value={f.spouse_name} onChange={e=>set("spouse_name",e.target.value)}/></Field>
        <Field label="Note" full><textarea rows={2} value={f.note} onChange={e=>set("note",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save}>Save</button></div>
    </Modal>
  );
}

function Inventory({ vendorId, vendor, items, reload }) {
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  const isTalentMgmt = (vendor?.category||"").toLowerCase().includes("talent");
  return (
    <div className="panel">
      <div className="panel-h"><h3>{isTalentMgmt?"Roster / Inventory":"Inventory / Roster"}</h3><button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-inventory"><Plus size={14}/> Add {isTalentMgmt?"Talent":"Item"}</button></div>
      <p className="muted small">{isTalentMgmt
        ? "Each talent has its own category + sub-category — searchable from the global search."
        : "Master price list — Rate(C) is what you charge the client, CTC rate is your cost."}</p>
      <table className="tbl"><thead><tr>
        <th>Name</th><th>Category</th><th>Sub-Category</th><th>Specialty</th>
        <th>Excl.</th><th>Unit</th>
        <th>Rate(C) ₹</th><th>CTC rate ₹</th><th>Profit / unit</th>
        <th></th>
      </tr></thead><tbody>
        {items.map(i=>{
          const rateC = parseFloat(i.rate_c ?? i.sell_price ?? 0)||0;
          const ctc = parseFloat(i.ctc_rate ?? i.b2b_price ?? i.price ?? 0)||0;
          const profit = rateC - ctc;
          return (<tr key={i.id}>
            <td><b>{i.item}</b>{i.description && <div className="muted small truncate" style={{maxWidth:200}}>{i.description}</div>}</td>
            <td className="small">{i.category||""}</td>
            <td className="small">{i.sub_category||""}</td>
            <td className="small">{i.specialty||""}</td>
            <td>{i.exclusive ? <span className="tag dark sm">✓</span> : ""}</td>
            <td>{i.unit}</td>
            <td><b>{rateC ? `₹${rateC.toLocaleString()}` : ""}</b></td>
            <td>{ctc ? `₹${ctc.toLocaleString()}` : ""}</td>
            <td style={{color: profit >= 0 ? "var(--burgundy)" : "var(--danger)"}}>
              {rateC && ctc ? `${profit>=0?"+":""}₹${profit.toLocaleString()}` : ""}
            </td>
            <td><button className="icon-btn" onClick={()=>setEdit(i)}><Edit3 size={14}/></button>
              <button className="icon-btn danger" onClick={async ()=>{if(!window.confirm(`Delete "${i.item}"?`)) return; await api.delete(`/inventory/${i.id}`);reload();}}><Trash2 size={14}/></button></td>
          </tr>);
        })}
        {items.length===0 && <tr><td colSpan={10} className="empty-tr">No {isTalentMgmt?"talents":"items"} yet. Click "Add" to add your first.</td></tr>}
      </tbody></table>
      {(add||edit) && <InvForm vendorId={vendorId} vendor={vendor} item={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);reload();}}/>}
    </div>
  );
}
function InvForm({ vendorId, vendor, item, onClose, onSaved }) {
  const isTalentMgmt = (vendor?.category||"").toLowerCase().includes("talent");
  const seed = item ? {
    ...item,
    rate_c: item.rate_c ?? item.sell_price ?? "",
    ctc_rate: item.ctc_rate ?? item.b2b_price ?? item.price ?? "",
  } : {item:"",category:"",sub_category:"",specialty:"",exclusive:false,description:"",unit:"",rate_c:"",ctc_rate:"",social_links:{instagram:"",youtube:"",facebook:""}};
  const [f,setF] = useState(seed);
  const [dirty, setDirty] = useState(false);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  const setSocial=(k,v)=>{ setF({...f,social_links:{...(f.social_links||{}),[k]:v}}); setDirty(true); };
  useUnsavedGuard(dirty);
  const profit = (() => {
    const r = parseFloat(f.rate_c||0)||0;
    const c = parseFloat(f.ctc_rate||0)||0;
    if (r && c) return r - c;
    return null;
  })();
  const save = async () => {
    if (!f.item?.trim()) { toast.error("Name is required"); return; }
    let payload = {...f};
    payload.rate_c = f.rate_c ? parseFloat(f.rate_c) : 0;
    payload.ctc_rate = f.ctc_rate ? parseFloat(f.ctc_rate) : 0;
    payload.sell_price = payload.rate_c;
    payload.b2b_price = payload.ctc_rate;
    if (item) await api.put(`/inventory/${item.id}`, payload);
    else await api.post(`/vendors/${vendorId}/inventory`, payload);
    setDirty(false);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={item?(isTalentMgmt?"Edit Talent":"Edit Item"):(isTalentMgmt?"Add Talent":"Add Inventory Item")} onClose={onClose} wide dirty={dirty}>
      <div className="form-grid">
        <Field label={isTalentMgmt?"Talent Name *":"Item Name *"}><input value={f.item} onChange={e=>set("item",e.target.value)} data-testid="inv-item" placeholder={isTalentMgmt?"e.g. Zephyr Band":"e.g. Premium Mandap"}/></Field>
        <Field label="Category"><Typeahead type="vendor_category" value={f.category||""} onChange={v=>set("category",v)}/></Field>
        <Field label="Sub-Category"><Typeahead type="vendor_sub_category" parent={f.category||""} value={f.sub_category||""} onChange={v=>set("sub_category",v)}/></Field>
        <Field label="Specialty"><input value={f.specialty||""} onChange={e=>set("specialty",e.target.value)} placeholder={isTalentMgmt?"Bollywood Retro / Sufi Fusion / EDM":"Free text"}/></Field>
        <Field label="Unit"><input value={f.unit} onChange={e=>set("unit",e.target.value)} placeholder="per night / per piece / per show"/></Field>
        <Field label="Exclusive?"><label className="chk"><input type="checkbox" checked={!!f.exclusive} onChange={e=>set("exclusive",e.target.checked)}/> Exclusive to this vendor</label></Field>
        <Field label="Rate(C) ₹"><input type="number" value={f.rate_c||""} onChange={e=>set("rate_c",e.target.value)} placeholder="e.g. 100000"/></Field>
        <Field label="CTC rate ₹"><input type="number" value={f.ctc_rate||""} onChange={e=>set("ctc_rate",e.target.value)} placeholder="e.g. 80000"/></Field>
        <Field label={profit !== null ? `Profit / unit: ${profit>=0?"+":""}₹${profit.toLocaleString()}` : "Profit / unit"}>
          <div className="muted small" style={{padding:"6px 10px"}}>Auto: Rate(C) − CTC rate.</div>
        </Field>
        {isTalentMgmt && <>
          <Field label="Instagram"><input value={f.social_links?.instagram||""} onChange={e=>setSocial("instagram",e.target.value)} placeholder="@handle"/></Field>
          <Field label="YouTube"><input value={f.social_links?.youtube||""} onChange={e=>setSocial("youtube",e.target.value)} placeholder="channel URL"/></Field>
          <Field label="Facebook"><input value={f.social_links?.facebook||""} onChange={e=>setSocial("facebook",e.target.value)} placeholder="page URL"/></Field>
        </>}
        <Field label="Description / Notes" full><textarea rows={2} value={f.description} onChange={e=>set("description",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-inventory">Save</button></div>
    </Modal>
  );
}

// ===== CLIENTS =====
function Clients() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(""); const [status, setStatus] = useState("");
  const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  const load = async () => { try {
    const p = {}; if (q) p.q=q; if (status) p.status=status;
    const r = await api.get("/clients", { params: p }); setRows(r.data);
  } catch {} };
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="single-col">
      <div className="page-h">
        <h2>Clients / Prospects</h2>
        <div className="row-btns">
          <input className="ipt" placeholder="Search…" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()}/>
          <select className="ipt" value={status} onChange={e=>{setStatus(e.target.value); setTimeout(load,0);}}>
            <option value="">All status</option><option>Pending</option><option>Finalised</option><option>On Hold</option><option>Lost</option>
          </select>
          <button className="btn-primary" onClick={()=>setAdd(true)} data-testid="add-client"><Plus size={14}/> Add Client</button>
        </div>
      </div>
      <RecentlyViewed kind="clients" onPick={(c)=>{setEdit(c); api.post(`/recent/clients/${c.id}`).catch(()=>{});}}/>
      <table className="tbl">
        <thead><tr><th>Name</th><th>Couple</th><th>Event Type</th><th>Event Dates</th><th>Services</th><th>Status</th><th>Mobile</th><th>Updated</th><th></th></tr></thead>
        <tbody>
        {rows.map(c=>(
          <tr key={c.id}>
            <td><b>{c.name}</b>{c.reference && <span className="muted small"> • ref: {c.reference}</span>}</td>
            <td>{[c.bride_name, c.groom_name].filter(Boolean).join(" & ")}</td>
            <td>{c.event_type}</td>
            <td className="small">{c.event_date_from||c.event_date}{c.event_date_to?` → ${c.event_date_to}`:""}</td>
            <td className="small">{(c.services_required||[]).join(", ")}</td>
            <td><span className={`status-pill s-${(c.status||"pending").toLowerCase().replace(/\s/g,"-")}`}>{c.status||"Pending"}</span></td>
            <td>{c.mobile}</td>
            <td className="small muted">{timeAgo(c.updated_at)}</td>
            <td><button className="icon-btn" onClick={()=>{setEdit(c); api.post(`/recent/clients/${c.id}`).catch(()=>{});}}><Edit3 size={14}/></button>
              <button className="icon-btn danger" data-testid={`del-client-${c.id}`}
                onClick={async()=>{if(!window.confirm(`Delete client "${c.name}"?`)) return; await api.delete(`/clients/${c.id}`); load();}}>
                <Trash2 size={14}/></button></td>
          </tr>
        ))}
        {rows.length===0 && <tr><td colSpan={9} className="empty-tr">No clients yet</td></tr>}
        </tbody>
      </table>
      {(add||edit) && <ClientForm client={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);load();}}/>}
    </div>
  );
}

function ClientForm({ client, onClose, onSaved, onCreated }) {
  const [f,setF] = useState(client||{name:"",groom_name:"",bride_name:"",mobile:"",email:"",
    event_type:"",event_date_from:"",event_date_to:"",budget:"",reference:"",
    status:"Pending",services_required:[],notes:""});
  const set=(k,v)=>setF({...f,[k]:v});
  const save = async () => {
    // auto-build name from couple if name blank
    let payload = {...f};
    if (!payload.name && (payload.bride_name || payload.groom_name)) {
      payload.name = [payload.bride_name, payload.groom_name].filter(Boolean).join(" & ");
    }
    const r = client ? await api.put(`/clients/${client.id}`, payload) : await api.post("/clients", payload);
    if (payload.status === "Lost") toast.warning(`Marked "${payload.name}" as Lost`);
    else toast.success("Saved");
    if (onCreated) onCreated(r.data);
    onSaved(r.data);
  };
  return (
    <Modal title={client?"Edit Client":"Add Client"} onClose={onClose} wide>
      <div className="form-grid">
        <Field label="Client Name *"><input value={f.name} onChange={e=>set("name",e.target.value)} data-testid="client-name" placeholder="Display name"/></Field>
        <Field label="Bride Name"><input value={f.bride_name} onChange={e=>set("bride_name",e.target.value)}/></Field>
        <Field label="Groom Name"><input value={f.groom_name} onChange={e=>set("groom_name",e.target.value)}/></Field>
        <Field label="Mobile"><input value={f.mobile} onChange={e=>set("mobile",e.target.value)}/></Field>
        <Field label="Email"><input value={f.email} onChange={e=>set("email",e.target.value)}/></Field>
        <Field label="Reference / Referred By"><input value={f.reference} onChange={e=>set("reference",e.target.value)}/></Field>
        <Field label="Event Type"><Typeahead type="event_type" value={f.event_type} onChange={v=>set("event_type",v)}/></Field>
        <Field label="Event Date From"><input type="date" value={f.event_date_from} onChange={e=>set("event_date_from",e.target.value)}/></Field>
        <Field label="Event Date To"><input type="date" value={f.event_date_to} onChange={e=>set("event_date_to",e.target.value)}/></Field>
        <Field label="Status"><select value={f.status} onChange={e=>set("status",e.target.value)}><option>Pending</option><option>Finalised</option><option>On Hold</option><option>Lost</option></select></Field>
        <Field label="Budget"><input value={f.budget} onChange={e=>set("budget",e.target.value)}/></Field>
        <Field label="Services Required" full><MultiSelect type="service_required" value={f.services_required||[]} onChange={v=>set("services_required",v)}/></Field>
        <Field label="Notes" full><textarea rows={3} value={f.notes} onChange={e=>set("notes",e.target.value)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-client">Save</button></div>
    </Modal>
  );
}

// ===== QUOTES =====
function Quotes({ parentType, parentId, parentName, standalone, rooms: hotelRooms }) {
  const [rows, setRows] = useState([]); const [add, setAdd] = useState(false); const [edit, setEdit] = useState(null);
  const load = async () => { try {
    const p = {}; if (parentType) p.parent_type = parentType;
    if (parentId) p.parent_id = parentId;
    const r = await api.get("/quotes", { params: p }); setRows(r.data);
  } catch {} };
  useEffect(()=>{ load(); /* eslint-disable-next-line */ }, [parentType, parentId]);
  return (
    <div className={standalone?"single-col":"panel"}>
      <div className={standalone?"page-h":"panel-h"}>
        <h3>{standalone?"All Quotes":"Quotes"} ({rows.length})</h3>
        <button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-quote"><Plus size={14}/> Add Quote</button>
      </div>
      <table className="tbl">
        <thead><tr><th>Date</th><th>{standalone?"For":""}</th><th>Client</th><th>Event</th><th>Pax</th><th>Amount(C)</th><th>CTC Amount</th><th>Profit</th><th>File</th><th></th></tr></thead>
        <tbody>
        {rows.map(q=>(<tr key={q.id}>
          <td>{q.quote_date}</td><td>{standalone?q.parent_name:""}</td><td>{q.client_name||"-"}</td>
          <td className="small">{q.event_date}</td><td>{q.pax}</td><td>{q.amount?`₹${parseInt(q.amount).toLocaleString()}`:""}</td>
          <td className="small">{q.ctc?`₹${parseInt(q.ctc).toLocaleString()}`:""}</td>
          <td className="small" style={{color: (q.profit||0) >= 0 ? "var(--burgundy)" : "var(--danger)"}}>
            {q.profit ? `${q.profit>=0?"+":""}₹${parseInt(q.profit).toLocaleString()}` : ""}
          </td>
          <td>{q.file_url && <a href={`${API.replace(/\/api$/,"")}${q.file_url}`} target="_blank" rel="noreferrer">📎</a>}</td>
          <td><button className="icon-btn" onClick={()=>setEdit(q)}><Edit3 size={14}/></button>
            <button className="icon-btn danger" onClick={async ()=>{if(!window.confirm("Delete this quote?")) return; await api.delete(`/quotes/${q.id}`);load();}}><Trash2 size={14}/></button></td>
        </tr>))}
        {rows.length===0 && <tr><td colSpan={10} className="empty-tr">No quotes</td></tr>}
        </tbody>
      </table>
      {(add||edit) && <QuoteForm quote={edit} parentType={parentType||edit?.parent_type} parentId={parentId||edit?.parent_id} parentName={parentName||edit?.parent_name} hotelRooms={hotelRooms} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);load();}}/>}
    </div>
  );
}

function QuoteForm({ quote, parentType, parentId, parentName, hotelRooms, onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [hotels, setHotels] = useState([]); const [vendors, setVendors] = useState([]);
  const [inclusionsMaster, setInclusionsMaster] = useState([]);
  const [vendorInventory, setVendorInventory] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [showAddClient, setShowAddClient] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [f, setF] = useState(quote||{quote_date:new Date().toISOString().substring(0,10),
    event_date:"", parent_type:parentType||"hotel", parent_id:parentId||"", parent_name:parentName||"",
    client_id:"", client_name:"", pax:"", amount:"", meal_plan:"", notes:"", file_url:"",
    room_rates: hotelRooms && hotelRooms.length ? [{category:"Run of House",rate:"",meal_plan:"EP"}, ...hotelRooms.map(r=>({category:r.room_type,rate:"",meal_plan:"EP"}))]
                : [{category:"Run of House",rate:"",meal_plan:"EP"}],
    breakfast_addon:"", lunch_addon:"", dinner_addon:"",
    inclusions:[], line_items:[]});
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  useEffect(()=>{
    api.get("/clients").then(r=>setClients(r.data)).catch(()=>{});
    api.get("/inclusions").then(r=>setInclusionsMaster(r.data)).catch(()=>{});
    if (!parentId) { api.get("/hotels").then(r=>setHotels(r.data)).catch(()=>{}); api.get("/vendors").then(r=>setVendors(r.data)).catch(()=>{}); }
  }, [parentId]);
  // Load vendor inventory + templates when parent is a vendor
  useEffect(()=>{
    if (f.parent_type==="vendor" && f.parent_id) {
      api.get(`/vendors/${f.parent_id}/inventory`).then(r=>setVendorInventory(r.data||[])).catch(()=>{});
      // Get vendor category to pull templates
      api.get(`/vendors/${f.parent_id}`).then(r=>{
        const cat = r.data?.category;
        if (cat) api.get("/quote-templates", { params:{ category: cat }}).then(rt=>setQuoteTemplates(rt.data||[])).catch(()=>{});
      }).catch(()=>{});
    } else {
      setVendorInventory([]); setQuoteTemplates([]);
    }
  }, [f.parent_type, f.parent_id]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/upload", fd); set("file_url", r.data.url);
  };
  // Vendor line items calculations
  const lineItems = f.line_items || [];
  const totals = lineItems.reduce((acc, li) => {
    const qty = parseFloat(li.qty||0)||0;
    const rateC = parseFloat(li.rate_c ?? li.sell_price ?? 0)||0;
    const ctc = parseFloat(li.ctc_rate ?? li.b2b_price ?? 0)||0;
    acc.amount_c += qty * rateC;
    acc.ctc_amount += qty * ctc;
    return acc;
  }, {amount_c:0, ctc_amount:0});
  totals.profit = totals.amount_c - totals.ctc_amount;
  const addLineItemFromInventory = (inv) => {
    const rateC = parseFloat(inv.rate_c ?? inv.sell_price ?? 0)||0;
    const ctc = parseFloat(inv.ctc_rate ?? inv.b2b_price ?? inv.price ?? 0)||0;
    set("line_items", [...lineItems, {inventory_id:inv.id, item:inv.item, unit:inv.unit||"", qty:1, rate_c:rateC, ctc_rate:ctc}]);
  };
  const addBlankLineItem = () => set("line_items", [...lineItems, {item:"",unit:"",qty:1,rate_c:0,ctc_rate:0}]);
  const updateLI = (i,k,v) => { const a=[...lineItems]; a[i] = {...a[i], [k]:v}; set("line_items", a); };
  const delLI = (i) => set("line_items", lineItems.filter((_,x)=>x!==i));
  const applyTemplate = (tpl) => {
    if (!tpl?.items?.length) { toast.info("Template has no items"); return; }
    const items = tpl.items.map(it => {
      // Try to match inventory by name
      const inv = vendorInventory.find(v => (v.item||"").toLowerCase() === (it.item||"").toLowerCase());
      const rateC = inv ? (parseFloat(inv.rate_c ?? inv.sell_price ?? 0)||0) : 0;
      const ctc = inv ? (parseFloat(inv.ctc_rate ?? inv.b2b_price ?? inv.price ?? 0)||0) : 0;
      return { inventory_id: inv?.id||"", item: it.item, unit: it.unit||(inv?.unit||""),
               qty: it.default_qty||1, rate_c: rateC, ctc_rate: ctc };
    });
    set("line_items", [...lineItems, ...items]);
    toast.success(`Loaded ${items.length} items from template`);
  };
  const save = async () => {
    let payload = {...f};
    // Auto-set total amount + ctc + profit from line items if vendor quote
    if (f.parent_type === "vendor" && lineItems.length > 0) {
      payload.amount = String(Math.round(totals.amount_c));
      payload.ctc = Math.round(totals.ctc_amount);
      payload.profit = Math.round(totals.profit);
    }
    if (quote) await api.put(`/quotes/${quote.id}`, payload); else await api.post("/quotes", payload);
    setDirty(false);
    toast.success("Saved"); onSaved();
  };
  const addRoomRate = () => set("room_rates", [...(f.room_rates||[]), {category:"",rate:"",meal_plan:"EP"}]);
  const updateRR = (i, k, v) => { const a=[...f.room_rates]; a[i]={...a[i],[k]:v}; set("room_rates",a); };
  const delRR = (i) => set("room_rates", f.room_rates.filter((_,x)=>x!==i));
  const toggleInclusion = (incl) => {
    const exists = (f.inclusions||[]).find(x=>x.name===incl.name);
    if (exists) set("inclusions", f.inclusions.filter(x=>x.name!==incl.name));
    else set("inclusions", [...(f.inclusions||[]), {name: incl.name, value: incl.default_value||""}]);
  };
  const updateInclusionVal = (name, v) => set("inclusions", f.inclusions.map(x=>x.name===name?{...x, value:v}:x));

  return (
    <Modal title={quote?"Edit Quote":"Add Quote"} onClose={onClose} wide dirty={dirty}>
      <div className="form-grid">
        {!parentId && <>
          <Field label="Quote For"><select value={f.parent_type} onChange={e=>set("parent_type",e.target.value)}>
            <option value="hotel">Hotel</option><option value="vendor">Vendor</option></select></Field>
          <Field label={f.parent_type==="hotel"?"Hotel":"Vendor"}>
            <select value={f.parent_id} onChange={e=>{const id=e.target.value; const o=(f.parent_type==="hotel"?hotels:vendors).find(x=>x.id===id); set("parent_id",id); set("parent_name", o?(o.venue_name||o.name):"");}}>
              <option value="">--</option>
              {(f.parent_type==="hotel"?hotels:vendors).map(x=><option key={x.id} value={x.id}>{x.venue_name||x.name}</option>)}
            </select>
          </Field>
        </>}
        <Field label="Quote Date"><input type="date" value={f.quote_date} onChange={e=>set("quote_date",e.target.value)}/></Field>
        <Field label="Event Date"><input type="date" value={f.event_date} onChange={e=>set("event_date",e.target.value)}/></Field>
        <Field label="Client">
          <div style={{display:"flex",gap:4}}>
            <select value={f.client_id} onChange={e=>{const id=e.target.value; const c=clients.find(x=>x.id===id); set("client_id",id); set("client_name",c?c.name:"");}} style={{flex:1}}>
              <option value="">-- select --</option>
              {clients.map(c=><option key={c.id} value={c.id}>{c.name}{c.event_type?` (${c.event_type})`:""}</option>)}
            </select>
            <button type="button" className="btn-secondary sm" onClick={()=>setShowAddClient(true)} data-testid="quote-add-client"><Plus size={12}/></button>
          </div>
        </Field>
        <Field label="No. of Pax"><input value={f.pax} onChange={e=>set("pax",e.target.value)}/></Field>
        <Field label="Total Amount"><input value={f.amount} onChange={e=>set("amount",e.target.value)}/></Field>
        <Field label="Meal Plan"><select value={f.meal_plan} onChange={e=>set("meal_plan",e.target.value)}>
          <option value="">--</option><option>EP (Room Only)</option><option>CP (Breakfast)</option>
          <option>MAP (Brkfst + 1)</option><option>AP (All Meals)</option></select></Field>
        {f.parent_type==="hotel" && <>
          <Field label="Breakfast Add-on"><input value={f.breakfast_addon} onChange={e=>set("breakfast_addon",e.target.value)}/></Field>
          <Field label="Lunch Add-on"><input value={f.lunch_addon} onChange={e=>set("lunch_addon",e.target.value)}/></Field>
          <Field label="Dinner Add-on"><input value={f.dinner_addon} onChange={e=>set("dinner_addon",e.target.value)}/></Field>
        </>}
        <Field label="Notes" full><textarea rows={2} value={f.notes} onChange={e=>set("notes",e.target.value)}/></Field>
        <Field label="Upload Quote (PDF/Excel)" full>
          <input type="file" onChange={e=>e.target.files[0]&&upload(e.target.files[0])}/>
          {f.file_url && <a href={`${API.replace(/\/api$/,"")}${f.file_url}`} target="_blank" rel="noreferrer"> 📎 View</a>}
        </Field>
      </div>
      {f.parent_type==="hotel" && (
        <div className="panel" style={{marginTop:10}}>
          <div className="panel-h"><h3>Room Category Rates</h3><button className="btn-primary sm" onClick={addRoomRate}><Plus size={14}/> Add Row</button></div>
          <table className="tbl">
            <thead><tr><th>Category</th><th>Rate</th><th>Meal Plan</th><th></th></tr></thead>
            <tbody>{(f.room_rates||[]).map((rr,i)=>(<tr key={i}>
              <td><Typeahead type="room_category" value={rr.category} onChange={v=>updateRR(i,"category",v)}/></td>
              <td><input value={rr.rate} onChange={e=>updateRR(i,"rate",e.target.value)}/></td>
              <td><select value={rr.meal_plan} onChange={e=>updateRR(i,"meal_plan",e.target.value)}><option>EP</option><option>CP</option><option>MAP</option><option>AP</option></select></td>
              <td><button className="icon-btn danger" onClick={()=>delRR(i)}><Trash2 size={14}/></button></td>
            </tr>))}</tbody>
          </table>
        </div>
      )}
      {f.parent_type==="hotel" && inclusionsMaster.length>0 && (
        <div className="panel" style={{marginTop:10}}>
          <h3>Inclusions</h3>
          <table className="tbl"><thead><tr><th>✓</th><th>Inclusion</th><th>Value / Note</th></tr></thead>
            <tbody>{inclusionsMaster.map(im=>{
              const sel = (f.inclusions||[]).find(x=>x.name===im.name);
              return (<tr key={im.id}>
                <td><input type="checkbox" checked={!!sel} onChange={()=>toggleInclusion(im)}/></td>
                <td>{im.name}</td>
                <td>{sel ? <input value={sel.value} onChange={e=>updateInclusionVal(im.name, e.target.value)} placeholder={im.default_value||""}/> : <span className="muted small">{im.default_value}</span>}</td>
              </tr>);
            })}</tbody></table>
        </div>
      )}
      {f.parent_type==="vendor" && f.parent_id && (
        <div className="panel" style={{marginTop:10}}>
          <div className="panel-h">
            <h3>Quote Line Items</h3>
            <div className="row-btns">
              {quoteTemplates.length>0 && (
                <select onChange={e=>{const t = quoteTemplates.find(x=>x.id===e.target.value); if(t) applyTemplate(t); e.target.value="";}} data-testid="quote-template-picker">
                  <option value="">📋 Load from template…</option>
                  {quoteTemplates.map(t=>(<option key={t.id} value={t.id}>{t.name} ({t.items?.length||0} items)</option>))}
                </select>
              )}
              {vendorInventory.length>0 && (
                <select onChange={e=>{const inv = vendorInventory.find(x=>x.id===e.target.value); if(inv) addLineItemFromInventory(inv); e.target.value="";}} data-testid="quote-inv-picker">
                  <option value="">+ Add from inventory…</option>
                  {vendorInventory.map(inv=>(<option key={inv.id} value={inv.id}>{inv.item}{inv.unit?` (${inv.unit})`:""}</option>))}
                </select>
              )}
              <button className="btn-primary sm" onClick={addBlankLineItem} data-testid="add-line-item"><Plus size={14}/> Custom Item</button>
            </div>
          </div>
          {lineItems.length === 0 ? (
            <div className="empty">No line items yet. Pick from inventory, load a template, or add a custom item.</div>
          ) : (
            <>
              <table className="tbl">
                <thead><tr>
                  <th>Item</th><th>Qty</th><th>Unit</th>
                  <th>Rate(C)</th><th>Amount(C)</th>
                  <th>CTC rate</th><th>CTC Amount</th>
                  <th>Profit</th><th></th>
                </tr></thead>
                <tbody>{lineItems.map((li,i)=>{
                  const qty = parseFloat(li.qty||0)||0;
                  const rateC = parseFloat(li.rate_c ?? li.sell_price ?? 0)||0;
                  const ctc = parseFloat(li.ctc_rate ?? li.b2b_price ?? 0)||0;
                  const amtC = qty*rateC;
                  const ctcAmt = qty*ctc;
                  const profit = amtC - ctcAmt;
                  return (<tr key={i}>
                    <td><input value={li.item} onChange={e=>updateLI(i,"item",e.target.value)} style={{minWidth:160}}/></td>
                    <td><input type="number" value={li.qty} onChange={e=>updateLI(i,"qty",e.target.value)} style={{width:60}} data-testid={`li-qty-${i}`}/></td>
                    <td><input value={li.unit||""} onChange={e=>updateLI(i,"unit",e.target.value)} style={{width:80}}/></td>
                    <td><input type="number" value={li.rate_c ?? li.sell_price ?? ""} onChange={e=>updateLI(i,"rate_c",e.target.value)} style={{width:90}} data-testid={`li-rate-c-${i}`}/></td>
                    <td className="small"><b>₹{Math.round(amtC).toLocaleString()}</b></td>
                    <td><input type="number" value={li.ctc_rate ?? li.b2b_price ?? ""} onChange={e=>updateLI(i,"ctc_rate",e.target.value)} style={{width:90}} data-testid={`li-ctc-rate-${i}`}/></td>
                    <td className="small">₹{Math.round(ctcAmt).toLocaleString()}</td>
                    <td className="small" style={{color: profit >= 0 ? "var(--burgundy)" : "var(--danger)"}}>
                      <b>{profit >= 0 ? "+" : ""}₹{Math.round(profit).toLocaleString()}</b>
                    </td>
                    <td><button className="icon-btn danger" onClick={()=>delLI(i)}><Trash2 size={14}/></button></td>
                  </tr>);
                })}</tbody>
                <tfoot>
                  <tr style={{fontWeight:600, background:"var(--tint)"}}>
                    <td colSpan={4} style={{textAlign:"right"}}>Totals →</td>
                    <td>₹{Math.round(totals.amount_c).toLocaleString()}</td>
                    <td></td>
                    <td>₹{Math.round(totals.ctc_amount).toLocaleString()}</td>
                    <td style={{color: totals.profit >= 0 ? "var(--burgundy)" : "var(--danger)"}}>
                      {totals.profit >= 0 ? "+" : ""}₹{Math.round(totals.profit).toLocaleString()}
                      {totals.amount_c > 0 && <span className="muted small"> ({Math.round((totals.profit/totals.amount_c)*100)}%)</span>}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
              <p className="muted small" style={{marginTop:6}}>💡 Total Amount field above will auto-update from <b>Amount(C)</b> on save.</p>
            </>
          )}
        </div>
      )}
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-quote">Save</button></div>
      {showAddClient && <ClientForm onClose={()=>setShowAddClient(false)} onSaved={()=>{setShowAddClient(false); api.get("/clients").then(r=>setClients(r.data));}} onCreated={(c)=>{set("client_id", c.id); set("client_name", c.name); setShowAddClient(false); api.get("/clients").then(r=>setClients(r.data));}}/>}
    </Modal>
  );
}
function QuotesPage() { return <Quotes standalone parentType={null} parentId={null}/>; }

// ===== Scan Inbox =====
function ScanInbox() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [batching, setBatching] = useState(false);
  const [batchProgress, setBatchProgress] = useState({done:0, total:0, errors:0});
  const load = async () => { try { const r = await api.get("/scan-inbox"); setItems(r.data); } catch {} };
  useEffect(()=>{ load(); }, []);
  const scan = async (file) => {
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/upload", fd);
      const fd2 = new FormData(); fd2.append("file", file);
      const r = await api.post("/ocr/business-card", fd2);
      const d = r.data||{};
      await api.post("/scan-inbox", {...d, image_url: up.data.url, reference:"", source:""});
      load();
      return true;
    } catch (e) { return false; }
  };
  const onSingleScan = async (file) => {
    if (await scan(file)) toast.success("Scanned & saved to inbox");
    else toast.error("Scan failed");
  };
  const onMultiScan = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    if (files.length > 10) { toast.error("Max 10 cards per batch"); return; }
    setBatching(true);
    setBatchProgress({done:0, total:files.length, errors:0});
    let done = 0, errors = 0;
    // Run sequentially to keep progress + rate-limit friendly
    for (const file of files) {
      const ok = await scan(file);
      done++; if (!ok) errors++;
      setBatchProgress({done, total: files.length, errors});
    }
    setBatching(false);
    toast.success(`Done: ${done-errors} saved${errors?` • ${errors} failed`:""}. Click each card below to review.`);
  };
  return (
    <div className="single-col">
      <div className="page-h">
        <h2>Scan Inbox <span className="muted small">({items.length} pending)</span></h2>
        <div className="row-btns">
          <label className="btn-secondary"><Camera size={14}/> Single Card
            <input type="file" accept="image/*" capture="environment" hidden onChange={e=>e.target.files[0]&&onSingleScan(e.target.files[0])}/>
          </label>
          <label className="btn-primary" data-testid="multi-card-upload">
            <Camera size={14}/> {batching ? `Scanning ${batchProgress.done}/${batchProgress.total}…` : "Bulk Upload (up to 10)"}
            <input type="file" accept="image/*" multiple hidden disabled={batching}
              onChange={e=>{onMultiScan(e.target.files); e.target.value="";}}/>
          </label>
        </div>
      </div>
      <p className="muted">Quickly scan cards at events; review and assign to a hotel/vendor later. <b>Bulk upload</b> processes up to 10 cards in one go.</p>
      {batching && (
        <div className="panel" style={{padding:"10px 14px", background:"var(--tint)", marginBottom:10}}>
          <b>Batch scanning {batchProgress.done}/{batchProgress.total}</b>
          {batchProgress.errors > 0 && <span className="muted small"> · {batchProgress.errors} failed so far</span>}
          <div style={{height:6, background:"#e5e7eb", borderRadius:3, marginTop:6, overflow:"hidden"}}>
            <div style={{height:"100%", width:`${(batchProgress.done/batchProgress.total)*100}%`, background:"var(--burgundy)"}}></div>
          </div>
        </div>
      )}
      <div className="contact-grid">
        {items.map(it=>(
          <div key={it.id} className="contact-card" onClick={()=>setEdit(it)}>
            {it.image_url && <img src={`${API.replace(/\/api$/,"")}${it.image_url}`} alt="" className="thumb"/>}
            <div className="cc-h"><b>{it.name||"(unnamed)"}</b></div>
            <div className="muted">{it.designation} {it.company && `• ${it.company}`}</div>
            {it.work_mobile && <div><Phone size={12}/> {it.work_mobile}</div>}
            {it.work_email && <div><Mail size={12}/> {it.work_email}</div>}
            {it.reference && <div className="muted small">📌 {it.reference}</div>}
          </div>
        ))}
        {items.length===0 && !batching && <div className="empty">Inbox is empty. Scan a card to start.</div>}
      </div>
      {edit && <ScanReviewModal item={edit} onClose={()=>{setEdit(null); load();}} onDeleted={()=>{setEdit(null); load();}}/>}
    </div>
  );
}

function ScanReviewModal({ item, onClose, onDeleted }) {
  const [f, setF] = useState(item);
  const [hotels, setHotels] = useState([]); const [vendors, setVendors] = useState([]);
  const [kind, setKind] = useState(null); // "hotel" | "vendor" | null
  const [target, setTarget] = useState({ id:"" });
  const [match, setMatch] = useState(null);
  const [dirty, setDirty] = useState(false);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  useDraft("scan-review-"+item.id, f, setF, true);
  useEffect(()=>{
    api.get("/hotels").then(r=>setHotels(r.data)).catch(()=>{});
    api.get("/vendors").then(r=>setVendors(r.data)).catch(()=>{});
  }, []);
  // Auto-match by company when kind is selected
  const tryMatch = async (forKind) => {
    setMatch(null); setTarget({ id:"" });
    if (!f.company) return;
    try {
      const r = await api.get(`/match/${forKind}`, { params: { name: f.company } });
      if (r.data && r.data.id) { setMatch(r.data); setTarget({ id: r.data.id }); }
    } catch {}
  };
  const chooseKind = (k) => { setKind(k); tryMatch(k); };

  const saveScan = async () => { await api.put(`/scan-inbox/${item.id}`, f); toast.success("Notes saved"); };

  const buildContactPayload = () => ({
    prefix: f.prefix||"", name: f.name||"", suffix: f.suffix||"",
    designation: f.designation||"", ref_code: f.ref_code||"",
    poc: !!f.poc, country: f.country||"India",
    work_email: f.work_email||"", personal_email: f.personal_email||"",
    work_mobile: f.work_mobile||"", personal_mobile: f.personal_mobile||"",
    dob: f.dob||"", anniversary: f.anniversary||"",
    spouse_name: f.spouse_name||"", spouse_dob: f.spouse_dob||"",
    vcard_front: f.image_url||"", note: f.note || (f.reference?`From: ${f.reference}`:"")
  });

  const saveAsContact = async () => {
    if (!kind) { toast.error("Pick Hotel or Vendor first"); return; }
    if (!target.id) { toast.error(`Pick a ${kind}`); return; }
    const payload = buildContactPayload();
    if (kind === "hotel") { payload.hotel_id = target.id; await api.post("/contacts", payload); }
    else { await api.post(`/vendors/${target.id}/contacts`, payload); }
    await api.delete(`/scan-inbox/${item.id}`);
    clearDraft("scan-review-"+item.id); setDirty(false);
    toast.success("Saved as contact"); onDeleted();
  };

  const quickCreate = async () => {
    if (!kind || !f.company) return;
    try {
      let id, name;
      if (kind === "hotel") {
        const r = await api.post("/hotels", { venue_name: f.company });
        id = r.data.id; name = r.data.venue_name;
      } else {
        const r = await api.post("/vendors", { name: f.company, company_name: f.company });
        id = r.data.id; name = r.data.name;
      }
      // assign contact to newly created
      const payload = buildContactPayload();
      if (kind === "hotel") { payload.hotel_id = id; await api.post("/contacts", payload); }
      else { await api.post(`/vendors/${id}/contacts`, payload); }
      await api.delete(`/scan-inbox/${item.id}`);
      toast.success(`Created ${kind} "${name}" and assigned contact`);
      onDeleted();
    } catch (e) { toast.error("Could not quick-create"); }
  };

  const saveStandalone = async () => {
    const payload = buildContactPayload();
    await api.post("/contacts", payload);
    await api.delete(`/scan-inbox/${item.id}`);
    toast.success("Saved as standalone contact (no hotel link)");
    onDeleted();
  };

  const del = async () => { if (!window.confirm("Discard this scan?")) return; await api.delete(`/scan-inbox/${item.id}`); onDeleted(); };

  const targetList = kind === "hotel" ? hotels : vendors;
  return (
    <Modal title="Review Scanned Card" onClose={onClose} wide dirty={dirty}>
      {f.image_url && <img src={`${API.replace(/\/api$/,"")}${f.image_url}`} alt="" className="thumb-lg"/>}
      {/* Step 1: choose kind */}
      <div className="panel" style={{marginBottom:12, background:"var(--tint)"}}>
        <h4 style={{margin:"0 0 8px", color:"var(--burgundy)"}}>Step 1 — Is this a Hotel or Vendor contact?</h4>
        <div className="row-btns">
          <button className={"btn-"+(kind==="hotel"?"primary":"ghost")} onClick={()=>chooseKind("hotel")}><Building2 size={14}/> Hotel</button>
          <button className={"btn-"+(kind==="vendor"?"primary":"ghost")} onClick={()=>chooseKind("vendor")}><Briefcase size={14}/> Vendor</button>
        </div>
      </div>

      <div className="form-grid compact">
        <Field label="Reference / Event" full><input value={f.reference||""} onChange={e=>set("reference",e.target.value)} placeholder="e.g. Bridal Asia 2026"/></Field>
        <Field label="Prefix"><Typeahead type="prefix" value={f.prefix||""} onChange={v=>set("prefix",v)}/></Field>
        <Field label="Name"><input value={f.name||""} onChange={e=>set("name",e.target.value)}/></Field>
        <Field label="Suffix"><input value={f.suffix||""} onChange={e=>set("suffix",e.target.value)}/></Field>
        <Field label="Designation"><Typeahead type="designation" value={f.designation||""} onChange={v=>set("designation",v)}/></Field>
        <Field label="Ref Code"><input value={f.ref_code||""} onChange={e=>set("ref_code",e.target.value)} placeholder="NSO / BDM …"/></Field>
        <Field label="POC?"><label className="chk"><input type="checkbox" checked={!!f.poc} onChange={e=>set("poc",e.target.checked)}/> Primary contact</label></Field>
        <Field label="Company / Hotel"><input value={f.company||""} onChange={e=>set("company",e.target.value)} onBlur={()=>kind&&tryMatch(kind)}/></Field>
        <Field label="Country"><Typeahead type="country" value={f.country||"India"} onChange={v=>set("country",v)}/></Field>
        <Field label="Work Email"><input value={f.work_email||""} onChange={e=>set("work_email",e.target.value)}/></Field>
        <Field label="Personal Email"><input value={f.personal_email||""} onChange={e=>set("personal_email",e.target.value)}/></Field>
        <Field label="Work Mobile"><input value={f.work_mobile||""} onChange={e=>set("work_mobile",e.target.value)}/></Field>
        <Field label="Personal Mobile"><input value={f.personal_mobile||""} onChange={e=>set("personal_mobile",e.target.value)}/></Field>
        <Field label="DOB"><input type="date" value={f.dob||""} onChange={e=>set("dob",e.target.value)}/></Field>
        <Field label="Anniversary"><input type="date" value={f.anniversary||""} onChange={e=>set("anniversary",e.target.value)}/></Field>
        <Field label="Spouse Name"><input value={f.spouse_name||""} onChange={e=>set("spouse_name",e.target.value)}/></Field>
        <Field label="Spouse DOB"><input type="date" value={f.spouse_dob||""} onChange={e=>set("spouse_dob",e.target.value)}/></Field>
        <Field label="Note" full><textarea rows={2} value={f.note||""} onChange={e=>set("note",e.target.value)} placeholder="Any extra info to remember"/></Field>
      </div>

      {/* Step 2: assign */}
      {kind && (
        <div className="panel" style={{marginTop:12}}>
          <h4 style={{margin:"0 0 8px", color:"var(--burgundy)"}}>Step 2 — Assign to {kind}</h4>
          {match && <div className="ai-match">✨ Auto-matched: <b>{match.venue_name || match.name}</b> ({Math.round(match.match_score*100)}% match)</div>}
          <div className="row-btns" style={{flexWrap:"wrap", alignItems:"flex-start"}}>
            <SearchableSelect
              items={targetList.map(x => ({ id: x.id, label: x.venue_name || x.name }))}
              value={target.id}
              onChange={(id)=>setTarget({id})}
              placeholder={`Type to search ${kind === "hotel" ? "hotels" : "vendors"}…`}
              testid="scan-assign-search"/>
            <button className="btn-primary" onClick={saveAsContact} disabled={!target.id}>Assign as Contact</button>
          </div>
          {f.company && !match && (
            <div style={{marginTop:10}}>
              <span className="muted small">No existing {kind} found matching "{f.company}". </span>
              <button className="btn-secondary sm" onClick={quickCreate}><Plus size={14}/> Create "{f.company}" as new {kind}</button>
            </div>
          )}
        </div>
      )}

      <div className="modal-actions" style={{justifyContent:"space-between"}}>
        <button className="btn-danger" onClick={del}><Trash2 size={14}/> Discard</button>
        <div className="row-btns">
          <button className="btn-ghost" onClick={saveScan}>Save Notes</button>
          <button className="btn-secondary" onClick={saveStandalone}>Save as Standalone Contact</button>
        </div>
      </div>
    </Modal>
  );
}

// ===== CALENDAR =====
function CalendarPage() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth()+1);
  const [year, setYear] = useState(today.getFullYear());
  const [events, setEvents] = useState([]);
  useEffect(()=>{ api.get("/calendar/events", { params:{ month }}).then(r=>setEvents(r.data)).catch(()=>{}); }, [month]);
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month-1, 1).getDay();
  const eventsByDay = useMemo(()=>{
    const m = {};
    events.forEach(e => { (m[e.day] ||= []).push(e); });
    return m;
  }, [events]);
  const monthName = new Date(year, month-1, 1).toLocaleString("default",{month:"long"});
  return (
    <div className="single-col">
      <div className="page-h">
        <h2>Birthdays & Anniversaries</h2>
        <div className="row-btns">
          <button className="btn-ghost sm" onClick={()=>{ if(month===1){setMonth(12); setYear(year-1);} else setMonth(month-1); }}>‹</button>
          <h3 style={{margin:0}}>{monthName} {year}</h3>
          <button className="btn-ghost sm" onClick={()=>{ if(month===12){setMonth(1); setYear(year+1);} else setMonth(month+1); }}>›</button>
        </div>
      </div>
      <div className="calendar">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=><div key={d} className="cal-head">{d}</div>)}
        {Array.from({length: firstDay}).map((_,i)=><div key={"e"+i} className="cal-cell empty"></div>)}
        {Array.from({length: daysInMonth}).map((_,i)=>{
          const day = i+1; const evs = eventsByDay[day]||[];
          const isToday = day===today.getDate() && month===today.getMonth()+1 && year===today.getFullYear();
          return (
            <div key={day} className={"cal-cell "+(isToday?"today":"")}>
              <div className="cal-d">{day}</div>
              {evs.map((e,idx)=>(<div key={idx} className={"cal-event "+(e.type==="Anniversary"?"e-anniv":e.type==="Spouse Birthday"?"e-spouse":"e-bday")}>
                {e.type==="Birthday"?"🎂":e.type==="Anniversary"?"💍":"🎂"} <b>{e.name}</b><span className="muted small"> {e.hotel_name||""}</span></div>))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== MAP =====
function MapPage() {
  const [hotels, setHotels] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/hotels").then(r=>setHotels(r.data)).catch(()=>{});
  useEffect(()=>{ load(); }, []);
  const parsed = hotels.map(h => {
    let lat = (typeof h.lat === "number") ? h.lat : null;
    let lng = (typeof h.lng === "number") ? h.lng : null;
    if ((lat===null || lng===null) && h.google_location) {
      const m = h.google_location.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/) || h.google_location.match(/(-?\d+\.\d+),(-?\d+\.\d+)/);
      if (m) { lat=parseFloat(m[1]); lng=parseFloat(m[2]); }
    }
    return { ...h, lat, lng };
  }).filter(h=>h.lat&&h.lng);
  const ql = q.trim().toLowerCase();
  const visible = ql ? parsed.filter(h => [h.venue_name, h.city, h.state, h.area, h.brand, h.sub_brand]
      .some(v => (v||"").toLowerCase().includes(ql))) : parsed;
  const backfill = async () => {
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/maintenance/backfill-geo");
      setMsg(`Resolved ${r.data.updated} hotel${r.data.updated===1?"":"s"} • ${r.data.failed} could not be resolved.`);
      await load();
    } catch(e) { setMsg(e.response?.data?.detail || "Backfill failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="single-col">
      <div className="page-h" style={{gap:12, flexWrap:"wrap"}}>
        <h2>Hotels on Map</h2>
        <input data-testid="map-filter" className="map-filter" placeholder="Filter on map (city, name, brand…)" value={q} onChange={e=>setQ(e.target.value)}/>
        <span className="muted">{visible.length} of {hotels.length} hotels shown</span>
        <button className="btn-secondary" data-testid="map-backfill" onClick={backfill} disabled={busy}>
          {busy ? "Resolving…" : "Resolve missing coordinates"}
        </button>
        {msg && <span className="muted">{msg}</span>}
      </div>
      <div style={{height:"calc(100vh - 180px)", border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden"}}>
        <LeafletMap points={visible}/>
      </div>
    </div>
  );
}
function LeafletMap({ points }) {
  const mapRef = useRef();
  const instanceRef = useRef(null);
  useEffect(()=>{
    let cancelled = false;
    (async () => {
      const L = await import("leaflet");
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !mapRef.current) return;
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });
      // Tear down any previous instance before re-creating
      if (instanceRef.current) {
        try { instanceRef.current.remove(); } catch {}
        instanceRef.current = null;
      }
      const map = L.map(mapRef.current).setView([20.5, 78.9], 5);
      instanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution:"© OSM" }).addTo(map);
      const bounds = [];
      points.forEach(p=>{
        const m = L.marker([p.lat, p.lng]).addTo(map);
        m.bindPopup(`<b>${p.venue_name}</b><br/>${[p.city,p.state].filter(Boolean).join(", ")}<br/>${p.no_of_rooms||""} rooms`);
        bounds.push([p.lat, p.lng]);
      });
      if (bounds.length) map.fitBounds(bounds, { padding:[40,40], maxZoom: 12 });
    })();
    return () => {
      cancelled = true;
      if (instanceRef.current) {
        try { instanceRef.current.remove(); } catch {}
        instanceRef.current = null;
      }
    };
  }, [points]);
  return <div ref={mapRef} style={{height:"100%", width:"100%"}}/>;
}

// ===== SETTINGS =====
function Settings() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  return (
    <div className="single-col">
      <div className="page-h"><h2>Settings</h2></div>
      <div className="tabs">{["users","inclusions","templates","import","export","maintenance","password","stats"].map(t=>(
        <button key={t} className={tab===t?"active":""} onClick={()=>setTab(t)}>{t}</button>))}</div>
      {tab==="users" && <UsersAdmin/>}
      {tab==="inclusions" && <InclusionsAdmin/>}
      {tab==="templates" && <QuoteTemplatesAdmin/>}
      {tab==="import" && <ImportPanel/>}
      {tab==="export" && (user?.is_super_admin ? <ExportPanel/> : <div className="panel"><p className="muted">Export is restricted to the super-admin account.</p></div>)}
      {tab==="maintenance" && <Maintenance/>}
      {tab==="password" && <PasswordChange/>}
      {tab==="stats" && <StatsPanel/>}
    </div>
  );
}

function Maintenance() {
  const nav = useNavigate();
  const [kind, setKind] = useState("hotels");
  const [dups, setDups] = useState([]);
  const [archived, setArchived] = useState([]);
  const [view, setView] = useState("duplicates");
  // Missing-data view state
  const REQUIRED_PRESETS = {
    hotels: ["venue_name","city","brand","no_of_rooms","google_location","pin_code"],
    contacts: ["name","designation","work_email","work_mobile","hotel_name"],
    vendors: ["name","category","city","mobile"],
    clients: ["name","mobile","event_type","status"],
    spaces: ["name","area","cap_theater","indoor_outdoor"],
    rooms: ["room_type","no_of_rooms","area"],
    dining: ["name","capacity"],
  };
  const [mdFields, setMdFields] = useState([]);
  const [mdRows, setMdRows] = useState([]);
  const [mdBusy, setMdBusy] = useState(false);
  const [mdRan, setMdRan] = useState(false);
  const [dedupBusy, setDedupBusy] = useState(false);
  const [dedupResult, setDedupResult] = useState(null);
  // Scan log
  const [scanLog, setScanLog] = useState([]);
  const [scanBusy, setScanBusy] = useState(false);
  const loadScanLog = async () => {
    setScanBusy(true);
    try { const r = await api.get("/scan-log"); setScanLog(r.data || []); }
    catch { setScanLog([]); }
    finally { setScanBusy(false); }
  };
  // Inline edit modal for the Missing Data review queue
  const [mdEditRecord, setMdEditRecord] = useState(null);  // {kind, record}
  // Open record in inline edit modal — no navigation away from Maintenance page
  const openRecord = async (rec) => {
    if (["spaces","rooms","dining"].includes(kind)) {
      // Sub-record — fetch parent hotel and open Hotel edit modal
      if (!rec.hotel_id) { toast.info(`Cannot open ${kind} directly — open the parent hotel.`); return; }
      try {
        const r = await api.get(`/hotels/${rec.hotel_id}`);
        setMdEditRecord({ kind: "hotels", record: r.data });
      } catch { toast.error("Couldn't load parent hotel"); }
      return;
    }
    setMdEditRecord({ kind, record: rec });
  };
  const handleMdSaved = () => {
    setMdEditRecord(null);
    // Refresh the missing-data list — saved rows with all required fields will disappear
    if (view === "missing") loadMissing();
  };
  const loadDups = async () => {
    try { const r = await api.get(`/duplicates/${kind}`); setDups(r.data); }
    catch { setDups([]); }
  };
  const loadArchived = async () => {
    try { const r = await api.get(`/archived/${kind}`); setArchived(r.data); }
    catch { setArchived([]); }
  };
  const loadMissing = async () => {
    if (!mdFields.length) { setMdRows([]); setMdRan(true); return; }
    setMdBusy(true);
    try {
      const r = await api.get("/maintenance/missing-data", { params: { kind, required: mdFields.join(",") }});
      setMdRows(r.data.records || []); setMdRan(true);
    } catch { setMdRows([]); setMdRan(true); }
    finally { setMdBusy(false); }
  };
  const runDedupLookups = async () => {
    if (!window.confirm("Normalise & merge duplicate lookup values (city/state/brand etc.)? Casing and whitespace differences will be merged.")) return;
    setDedupBusy(true); setDedupResult(null);
    try {
      const r = await api.post("/maintenance/dedupe-lookups");
      setDedupResult(r.data); toast.success(r.data.message || "Lookups deduped");
    } catch (e) { toast.error(e?.response?.data?.detail || "Dedup failed"); }
    finally { setDedupBusy(false); }
  };
  useEffect(()=>{
    if (view==="duplicates") loadDups();
    else if (view==="archived") loadArchived();
    else if (view==="scanlog") loadScanLog();
    else if (view==="missing") {
      // Reset fields to defaults for new kind
      setMdFields(REQUIRED_PRESETS[kind] || []);
      setMdRows([]); setMdRan(false);
    }
    /* eslint-disable-next-line */
  }, [kind, view]);
  const labelOf = (r) => r.venue_name || r.name || r.email || "(unnamed)";
  const subOf = (r) => [r.city, r.brand || r.category || r.event_type, r.work_mobile || r.mobile].filter(Boolean).join(" • ");
  const deleteRec = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    const url = kind === "contacts" ? `/contacts/${id}` : `/${kind}/${id}`;
    try { await api.delete(url); toast.success("Deleted"); loadDups(); }
    catch (e) { toast.error("Failed"); }
  };
  const restore = async (id) => {
    try { await api.post(`/restore/${kind}/${id}`); toast.success("Restored"); loadArchived(); }
    catch { toast.error("Failed"); }
  };
  const purge = async (id) => {
    if (!window.confirm("Permanently delete? This cannot be undone.")) return;
    try { await api.delete(`/purge/${kind}/${id}`); toast.success("Purged"); loadArchived(); }
    catch { toast.error("Failed"); }
  };
  return (
    <div className="panel">
      <div className="panel-h">
        <h3>Maintenance</h3>
        <div className="row-btns">
          <select value={kind} onChange={e=>setKind(e.target.value)} data-testid="maintenance-kind">
            <option value="hotels">Hotels</option><option value="contacts">Hotel Contacts</option>
            <option value="vendors">Vendors</option><option value="clients">Clients</option>
            {view==="missing" && <>
              <option value="spaces">Spaces (sub)</option>
              <option value="rooms">Rooms (sub)</option>
              <option value="dining">Dining (sub)</option>
            </>}
          </select>
          <div className="seg">
            <button className={view==="duplicates"?"on":""} onClick={()=>setView("duplicates")} data-testid="view-duplicates">Duplicates</button>
            <button className={view==="missing"?"on":""} onClick={()=>setView("missing")} data-testid="view-missing">Missing Data</button>
            <button className={view==="archived"?"on":""} onClick={()=>setView("archived")} data-testid="view-archived">Archived</button>
            <button className={view==="lookups"?"on":""} onClick={()=>setView("lookups")} data-testid="view-lookups">Lookups</button>
            <button className={view==="scanlog"?"on":""} onClick={()=>setView("scanlog")} data-testid="view-scanlog">Scan Log</button>
          </div>
        </div>
      </div>
      {view==="duplicates" && (
        <>
          <p className="muted small">Suspected duplicates: same name + same city (or matching mobile/email).</p>
          {dups.length===0 && <div className="empty">No duplicates found 🎉</div>}
          {dups.map((g,i)=>(
            <div key={i} className="dup-group">
              <div className="dup-h">Group {i+1}: {g.length} records</div>
              {g.map(r=>(
                <div key={r.id} className="dup-row">
                  <div><b>{labelOf(r)}</b><div className="muted small">{subOf(r)}</div>
                    <div className="muted small">Created {timeAgo(r.created_at)} • Updated {timeAgo(r.updated_at)}</div></div>
                  <button className="btn-danger sm" onClick={()=>deleteRec(r.id)}><Trash2 size={14}/> Delete</button>
                </div>
              ))}
            </div>
          ))}
        </>
      )}
      {view==="missing" && (
        <>
          <p className="muted small">Find records where any of these fields are empty. Toggle fields → Run report.</p>
          <div className="row-btns" style={{flexWrap:"wrap", marginBottom:8}}>
            {(REQUIRED_PRESETS[kind] || []).map(f => (
              <label key={f} className="chk" style={{marginRight:8}}>
                <input type="checkbox" checked={mdFields.includes(f)}
                  onChange={e => setMdFields(e.target.checked ? [...mdFields, f] : mdFields.filter(x=>x!==f))}/>
                {f}
              </label>
            ))}
            <button className="btn-primary sm" onClick={loadMissing} disabled={mdBusy || mdFields.length===0} data-testid="run-missing-report">
              {mdBusy ? "Searching…" : "Run report"}
            </button>
          </div>
          {mdRan && mdRows.length === 0 && <div className="empty">✨ No records missing the selected fields — everything is filled in.</div>}
          {mdRows.length > 0 && (
            <table className="tbl">
              <thead><tr><th>Name</th><th>Missing Fields</th><th>Details</th><th>Updated</th><th></th></tr></thead>
              <tbody>{mdRows.map(r=>{
                const missing = mdFields.filter(f => !r[f] || (Array.isArray(r[f]) && r[f].length===0));
                return (
                  <tr key={r.id}>
                    <td><b>{labelOf(r)}</b></td>
                    <td><div className="tags">{missing.map(m=><span key={m} className="tag" style={{background:"#fee2e2",color:"#991b1b"}}>{m}</span>)}</div></td>
                    <td className="muted small">{subOf(r)}</td>
                    <td className="muted small">{timeAgo(r.updated_at)}</td>
                    <td>
                      <button className="btn-primary sm" onClick={()=>openRecord(r)} data-testid={`md-open-${r.id}`}>
                        Open →
                      </button>
                    </td>
                  </tr>
                );
              })}</tbody>
            </table>
          )}
          {mdRows.length > 0 && (
            <p className="muted small" style={{marginTop:8}}>💡 Click "Open →" to navigate to the {kind} page, then click the record to edit and fill the missing fields.</p>
          )}
        </>
      )}
      {view==="lookups" && (
        <>
          <p className="muted small">Lookup hygiene: normalise casing &amp; whitespace, then merge duplicates (e.g. "siliguri", "SILIGURI", " Siliguri " → "Siliguri"). Affects city, state, brand, designation, etc.</p>
          <div className="row-btns" style={{marginTop:8}}>
            <button className="btn-primary" onClick={runDedupLookups} disabled={dedupBusy} data-testid="dedup-lookups-btn">
              {dedupBusy ? "Running…" : "Normalise & merge duplicate lookups"}
            </button>
          </div>
          {dedupResult && (
            <div className="muted" style={{marginTop:10, padding:"10px 12px", background:"var(--tint)", borderRadius:6}}>
              ✅ Merged <b>{dedupResult.merged}</b> duplicate entries • Removed <b>{dedupResult.removed}</b> blank entries.
            </div>
          )}
        </>
      )}
      {view==="scanlog" && (
        <>
          <p className="muted small">Every business card and fact-sheet scan you've run is logged here. Last 200 entries, newest first.</p>
          <div className="row-btns" style={{marginBottom:8}}>
            <button className="btn-ghost sm" onClick={loadScanLog} disabled={scanBusy} data-testid="reload-scanlog">
              {scanBusy ? "Loading…" : "↻ Refresh"}
            </button>
            <span className="muted small">{scanLog.length} entries</span>
          </div>
          {scanLog.length === 0 ? (
            <div className="empty">No scans yet. Scans appear here when you use 'Scan Card' or 'Scan Fact Sheet'.</div>
          ) : (
            <table className="tbl">
              <thead><tr><th>When</th><th>Type</th><th>Filename</th><th>Result</th></tr></thead>
              <tbody>{scanLog.map(s => (
                <tr key={s.id}>
                  <td className="small">{timeAgo(s.created_at)}<div className="muted small" style={{fontSize:10}}>{(s.created_at||"").substring(0,16).replace("T"," ")}</div></td>
                  <td>{s.scan_type === "fact-sheet" ? <span className="tag dark sm">📄 Fact Sheet</span> : <span className="tag lime sm">💳 Card</span>}</td>
                  <td className="small truncate" style={{maxWidth:260}}>{s.filename || "—"}</td>
                  <td className="small">{s.summary || <span className="muted">(no name extracted)</span>}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </>
      )}
      {view==="archived" && (
        <>
          <p className="muted small">Soft-deleted records — restore or permanently delete.</p>
          {archived.length===0 && <div className="empty">No archived records</div>}
          <table className="tbl"><thead><tr><th>Name</th><th>Details</th><th>Updated</th><th></th></tr></thead>
            <tbody>{archived.map(r=>(<tr key={r.id}>
              <td><b>{labelOf(r)}</b></td>
              <td className="muted small">{subOf(r)}</td>
              <td className="muted small">{timeAgo(r.updated_at)}</td>
              <td><button className="btn-secondary sm" onClick={()=>restore(r.id)}>Restore</button>
                <button className="btn-danger sm" style={{marginLeft:4}} onClick={()=>purge(r.id)}><Trash2 size={12}/> Purge</button></td>
            </tr>))}</tbody></table>
        </>
      )}
      {mdEditRecord && mdEditRecord.kind === "hotels" &&
        <HotelForm hotel={mdEditRecord.record} onClose={()=>setMdEditRecord(null)} onSaved={handleMdSaved}/>}
      {mdEditRecord && mdEditRecord.kind === "contacts" &&
        <ContactForm hotelId={mdEditRecord.record.hotel_id} hotelName={mdEditRecord.record.hotel_name}
                     contact={mdEditRecord.record} onClose={()=>setMdEditRecord(null)} onSaved={handleMdSaved}/>}
      {mdEditRecord && mdEditRecord.kind === "vendors" &&
        <VendorForm vendor={mdEditRecord.record} onClose={()=>setMdEditRecord(null)} onSaved={handleMdSaved}/>}
      {mdEditRecord && mdEditRecord.kind === "clients" &&
        <ClientForm client={mdEditRecord.record} onClose={()=>setMdEditRecord(null)} onSaved={handleMdSaved}/>}
    </div>
  );
}

function PasswordChange() {
  const [oldPw, setOld] = useState(""); const [newPw, setNew] = useState("");
  const save = async () => {
    try { await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated"); setOld(""); setNew("");
    } catch (e) { toast.error(e?.response?.data?.detail||"Failed"); }
  };
  return (
    <div className="panel">
      <h3>Change Password</h3>
      <div className="form-grid">
        <Field label="Current Password"><input type="password" value={oldPw} onChange={e=>setOld(e.target.value)}/></Field>
        <Field label="New Password"><input type="password" value={newPw} onChange={e=>setNew(e.target.value)}/></Field>
      </div>
      <button className="btn-primary" style={{marginTop:12}} onClick={save}><KeyRound size={14}/> Update Password</button>
    </div>
  );
}

function QuoteTemplatesAdmin() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [add, setAdd] = useState(false);
  const load = async () => { try { const r = await api.get("/quote-templates"); setRows(r.data||[]); } catch {} };
  useEffect(()=>{ load(); }, []);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Quote Templates (per Vendor Category)</h3>
        <button className="btn-primary sm" onClick={()=>setAdd(true)} data-testid="add-template"><Plus size={14}/> Add Template</button></div>
      <p className="muted small">When a quote is created for a vendor matching the category, you'll be able to "Load from template" to pre-fill line items.</p>
      <table className="tbl"><thead><tr><th>Name</th><th>Category</th><th>Items</th><th></th></tr></thead>
        <tbody>
          {rows.map(t=>(<tr key={t.id}><td><b>{t.name}</b></td><td>{t.category}</td>
            <td className="small">{(t.items||[]).map(i=>i.item).slice(0,3).join(", ")}{(t.items||[]).length>3?` +${t.items.length-3}`:""}</td>
            <td><button className="icon-btn" onClick={()=>setEdit(t)}><Edit3 size={14}/></button>
              <button className="icon-btn danger" onClick={async()=>{if(!window.confirm(`Delete template "${t.name}"?`)) return; await api.delete(`/quote-templates/${t.id}`); load();}}><Trash2 size={14}/></button></td>
          </tr>))}
          {rows.length===0 && <tr><td colSpan={4} className="empty-tr">No templates yet</td></tr>}
        </tbody></table>
      {(add||edit) && <QuoteTemplateForm template={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);load();}}/>}
    </div>
  );
}
function QuoteTemplateForm({ template, onClose, onSaved }) {
  const [f, setF] = useState(template || {name:"", category:"", items:[]});
  const [dirty, setDirty] = useState(false);
  const set=(k,v)=>{ setF({...f,[k]:v}); setDirty(true); };
  useUnsavedGuard(dirty);
  const addItem = () => set("items", [...(f.items||[]), {item:"", unit:"", default_qty:1}]);
  const updateItem = (i,k,v) => { const a=[...(f.items||[])]; a[i] = {...a[i], [k]:v}; set("items", a); };
  const delItem = (i) => set("items", (f.items||[]).filter((_,x)=>x!==i));
  const save = async () => {
    if (!f.name?.trim() || !f.category?.trim()) { toast.error("Name & Category are required"); return; }
    if (template) await api.put(`/quote-templates/${template.id}`, f);
    else await api.post("/quote-templates", f);
    setDirty(false);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={template?"Edit Template":"Add Quote Template"} onClose={onClose} wide dirty={dirty}>
      <div className="form-grid">
        <Field label="Template Name *"><input value={f.name} onChange={e=>set("name",e.target.value)} placeholder="e.g. Standard Wedding Decor Package" data-testid="tpl-name"/></Field>
        <Field label="Vendor Category *"><Typeahead type="vendor_category" value={f.category} onChange={v=>set("category",v)}/></Field>
      </div>
      <div className="panel" style={{marginTop:10}}>
        <div className="panel-h"><h4 style={{margin:0}}>Items in this template</h4>
          <button className="btn-primary sm" onClick={addItem}><Plus size={14}/> Add Item</button></div>
        <table className="tbl"><thead><tr><th>Item Name</th><th>Unit</th><th>Default Qty</th><th></th></tr></thead>
          <tbody>
            {(f.items||[]).map((it,i)=>(<tr key={i}>
              <td><input value={it.item} onChange={e=>updateItem(i,"item",e.target.value)} placeholder="e.g. Premium Mandap"/></td>
              <td><input value={it.unit||""} onChange={e=>updateItem(i,"unit",e.target.value)} placeholder="per day"/></td>
              <td><input type="number" value={it.default_qty||1} onChange={e=>updateItem(i,"default_qty",parseFloat(e.target.value)||0)} style={{width:80}}/></td>
              <td><button className="icon-btn danger" onClick={()=>delItem(i)}><Trash2 size={14}/></button></td>
            </tr>))}
            {(f.items||[]).length===0 && <tr><td colSpan={4} className="empty-tr">No items yet</td></tr>}
          </tbody></table>
        <p className="muted small" style={{marginTop:6}}>💡 When loaded into a quote, item names will be matched against the vendor's inventory to auto-fetch B2B / Sell prices.</p>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save} data-testid="save-template">Save</button></div>
    </Modal>
  );
}

function InclusionsAdmin() {
  const [rows, setRows] = useState([]); const [edit, setEdit] = useState(null); const [add, setAdd] = useState(false);
  const load = async () => { const r = await api.get("/inclusions"); setRows(r.data); };
  useEffect(()=>{ load(); }, []);
  return (
    <div className="panel">
      <div className="panel-h"><h3>Inclusions Master</h3><button className="btn-primary sm" onClick={()=>setAdd(true)}><Plus size={14}/> Add</button></div>
      <p className="muted small">These appear as a checklist when you add a quote. Defaults can be overridden per quote.</p>
      <table className="tbl"><thead><tr><th>Name</th><th>Default Value</th><th>Order</th><th></th></tr></thead><tbody>
        {rows.map(r=>(<tr key={r.id}><td>{r.name}</td><td>{r.default_value}</td><td>{r.order}</td>
          <td><button className="icon-btn" onClick={()=>setEdit(r)}><Edit3 size={14}/></button>
          <button className="icon-btn danger" onClick={async()=>{await api.delete(`/inclusions/${r.id}`); load();}}><Trash2 size={14}/></button></td></tr>))}
      </tbody></table>
      {(add||edit) && <InclusionForm inclusion={edit} onClose={()=>{setAdd(false);setEdit(null);}} onSaved={()=>{setAdd(false);setEdit(null);load();}}/>}
    </div>
  );
}
function InclusionForm({ inclusion, onClose, onSaved }) {
  const [f,setF] = useState(inclusion||{name:"",default_value:"",order:99});
  const set=(k,v)=>setF({...f,[k]:v});
  const save = async () => {
    if (inclusion) await api.put(`/inclusions/${inclusion.id}`, f); else await api.post("/inclusions", f);
    toast.success("Saved"); onSaved();
  };
  return (
    <Modal title={inclusion?"Edit Inclusion":"Add Inclusion"} onClose={onClose}>
      <div className="form-grid">
        <Field label="Name *"><input value={f.name} onChange={e=>set("name",e.target.value)}/></Field>
        <Field label="Default Value"><input value={f.default_value} onChange={e=>set("default_value",e.target.value)} placeholder="e.g. Complimentary, 15%"/></Field>
        <Field label="Order"><input type="number" value={f.order} onChange={e=>set("order",parseInt(e.target.value)||99)}/></Field>
      </div>
      <div className="modal-actions"><button className="btn-ghost" onClick={onClose}>Cancel</button><button className="btn-primary" onClick={save}>Save</button></div>
    </Modal>
  );
}

function UsersAdmin() {
  const [rows,setRows] = useState([]); const [show,setShow] = useState(false);
  const [f,setF] = useState({email:"",password:"",name:"",role:"member"});
  const load = async ()=>{ const r=await api.get("/users"); setRows(r.data); };
  useEffect(()=>{ load(); },[]);
  const create = async () => { try { await api.post("/users",f); toast.success("User created"); setShow(false); setF({email:"",password:"",name:"",role:"member"}); load(); } catch(e){ toast.error(e?.response?.data?.detail||"Failed"); } };
  return (
    <div className="panel">
      <div className="panel-h"><h3>Team Members</h3><button className="btn-primary sm" onClick={()=>setShow(true)}><Plus size={14}/> Add User</button></div>
      <table className="tbl"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th></th></tr></thead><tbody>
        {rows.map(u=>(<tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td><span className={"tag "+(u.role==="admin"?"lime":"dark")}>{u.role}</span></td>
          <td><button className="icon-btn danger" onClick={async ()=>{if(window.confirm("Delete user?")){await api.delete(`/users/${u.id}`);load();}}}><Trash2 size={14}/></button></td></tr>))}
      </tbody></table>
      <p className="muted small"><b>Admin</b>: full access, can delete. <b>Member</b>: create/edit, no delete. <b>Super-admin (aditya@)</b>: only one with Export rights.</p>
      {show && <Modal title="Add User" onClose={()=>setShow(false)}>
        <div className="form-grid">
          <Field label="Name *"><input value={f.name} onChange={e=>setF({...f,name:e.target.value})}/></Field>
          <Field label="Email *"><input type="email" value={f.email} onChange={e=>setF({...f,email:e.target.value})}/></Field>
          <Field label="Password *"><input type="password" value={f.password} onChange={e=>setF({...f,password:e.target.value})}/></Field>
          <Field label="Role"><select value={f.role} onChange={e=>setF({...f,role:e.target.value})}><option value="member">Member</option><option value="admin">Admin</option></select></Field>
        </div>
        <div className="modal-actions"><button className="btn-ghost" onClick={()=>setShow(false)}>Cancel</button><button className="btn-primary" onClick={create}>Create</button></div>
      </Modal>}
    </div>
  );
}

function ImportPanel() {
  const [kind, setKind] = useState("hotels");
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]); const [sample, setSample] = useState([]); const [total, setTotal] = useState(0);
  const [workbookSheets, setWorkbookSheets] = useState(null);
  const [mapping, setMapping] = useState({}); const [result, setResult] = useState(null); const [busy, setBusy] = useState(false);
  const sysFields = {
    hotels: ["venue_name","country","state","city","area","pin_code","brand","sub_brand","no_of_rooms","google_location","distance_airport","travel_time_road","pref_season","avoid_season","website","preferred","categories","types","ratings","rooms_note","venue_notes","general_notes","imp_notes"],
    contacts: ["name","prefix","suffix","designation","ref_code","work_email","personal_email","work_mobile","personal_mobile","dob","anniversary","spouse_name","note","hotel_name"],
    vendors: ["name","company_name","category","sub_category","city","state","country","address","website","email","instagram","mobile","notes"],
    clients: ["name","bride_name","groom_name","mobile","email","event_type","event_date_from","event_date_to","budget","reference","notes"],
  }[kind];
  const autoMap = (hdrs) => {
    const m = {};
    const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g,"");
    const fieldNorm = sysFields.map(f => [norm(f), f]).sort((a,b)=>b[0].length-a[0].length);
    const aliases = {
      "venuename":"venue_name","hotelname":"venue_name","property":"venue_name","propertyname":"venue_name",
      "rooms":"no_of_rooms","noofrooms":"no_of_rooms","totalrooms":"no_of_rooms",
      "googlelocation":"google_location","maplink":"google_location","location":"google_location",
      "mobile":"work_mobile","phone":"work_mobile","contact":"work_mobile","workmobile":"work_mobile","personalmobile":"personal_mobile",
      "email":"work_email","emailid":"work_email","workemail":"work_email","personalemail":"personal_email",
      "fullname":"name","contactname":"name","clientname":"name",
      "category":"categories","categories":"categories",
      "type":"types","types":"types","propertytype":"types",
      "rating":"ratings","ratings":"ratings","star":"ratings",
      "subbrand":"sub_brand","brand":"brand",
      "label":"labels","labels":"labels","tags":"labels",
      "preferred":"preferred","pref":"preferred",
      "pincode":"pin_code","pin":"pin_code","zip":"pin_code",
      "website":"website","url":"website",
      "designation":"designation","title":"designation","position":"designation",
      "refcode":"ref_code","ref":"ref_code","code":"ref_code",
      "dob":"dob","birthday":"dob","spouse":"spouse_name","anniversary":"anniversary",
      "notes":"notes","note":"notes","remarks":"notes",
      "venuenotes":"venue_notes","roomsnote":"rooms_note","roomnotes":"rooms_note",
      "generalnotes":"general_notes","importantnotes":"imp_notes","impnotes":"imp_notes",
      "distancefromairport":"distance_airport","airportdistance":"distance_airport","airport":"distance_airport",
      "traveltime":"travel_time_road","traveltimebyroad":"travel_time_road","drivetime":"travel_time_road",
      "preferredseason":"pref_season","avoidseason":"avoid_season",
      "city":"city","state":"state","country":"country","area":"area",
      "subcategory":"sub_category", "company":"company_name","companyname":"company_name",
      "insta":"instagram","instagram":"instagram",
      "bride":"bride_name","groom":"groom_name","ref":"reference","reference":"reference",
      "eventdate":"event_date_from","eventdatefrom":"event_date_from","eventdateto":"event_date_to",
      "eventtype":"event_type","budget":"budget",
    };
    hdrs.forEach(h => {
      const n = norm(h);
      if (aliases[n] && sysFields.includes(aliases[n])) { m[h]=aliases[n]; return; }
      const hit = fieldNorm.find(([fn]) => fn===n);
      if (hit) { m[h] = hit[1]; return; }
      const partial = fieldNorm.find(([fn]) => fn.includes(n) || n.includes(fn));
      if (partial) m[h] = partial[1];
    });
    return m;
  };
  const onFile = async (e) => {
    const f = e.target.files[0]; if(!f) return; setFile(f); setResult(null); setBusy(true);
    setWorkbookSheets(null); setHeaders([]); setSample([]); setMapping({});
    try { const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/import/preview", fd);
      if (r.data.is_workbook) {
        setWorkbookSheets(r.data.sheets || []);
      } else {
        setHeaders(r.data.headers||[]); setSample(r.data.sample||[]); setTotal(r.data.total||0);
        setMapping(autoMap(r.data.headers||[]));
      }
    } catch { toast.error("Could not read file"); }
    finally { setBusy(false); }
  };
  const doImport = async () => {
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); fd.append("mapping", JSON.stringify(mapping));
      const r = await api.post(`/import/${kind}`, fd);
      setResult(r.data); toast.success(`Imported ${r.data.inserted} • ${r.data.new_lookups||0} new dropdown values`);
    } catch { toast.error("Import failed"); }
    finally { setBusy(false); }
  };
  const downloadTemplate = async () => {
    if (kind === "hotels") {
      try {
        const r = await api.get("/import/template/hotels", { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = "ivory_vine_hotel_import_template.xlsx"; a.click();
      } catch { toast.error("Could not download template"); }
      return;
    }
    if (kind === "contacts" || kind === "vendor-contacts") {
      try {
        const path = kind === "contacts" ? "/import/template/contacts" : "/import/template/vendor-contacts";
        const r = await api.get(path, { responseType: "blob" });
        const url = URL.createObjectURL(r.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${kind}_template.csv`; a.click();
      } catch { toast.error("Could not download template"); }
      return;
    }
    const blob = new Blob([sysFields.join(",")+"\n"], { type:"text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `${kind}_template.csv`; a.click();
  };
  const doWorkbookImport = async () => {
    if (!file) { toast.error("Pick the .xlsx file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/import/hotels-workbook", fd);
      const d = r.data;
      const sub = (n,u) => `${n}${u?`+${u}u`:""}`;
      const msg = `Hotels: +${d.hotels_inserted} new, ${d.hotels_updated} updated • Spaces: ${sub(d.spaces, d.spaces_updated)} • Rooms: ${sub(d.rooms, d.rooms_updated)} • Dining: ${sub(d.dining, d.dining_updated)} • Contacts: ${sub(d.contacts, d.contacts_updated)}${d.unlinked?` • ${d.unlinked} unlinked rows`:""}`;
      setResult(d); toast.success(msg);
    } catch (e) { toast.error(e.response?.data?.detail || "Workbook import failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="panel">
      <h3>Import from Excel (.xlsx) or CSV</h3>
      <p className="muted">Headers auto-mapped. New brands, cities, categories etc. are added to dropdowns automatically.{kind==="hotels" && <> For hotels with banquet/room/dining data, use the <b>multi-sheet template</b> below and click <b>"Import full workbook"</b>.</>}</p>
      <div className="row-btns" style={{flexWrap:"wrap"}}>
        <select value={kind} onChange={e=>{setKind(e.target.value);setHeaders([]);setMapping({});setSample([]);setResult(null);setWorkbookSheets(null);}}>
          <option value="hotels">Hotels</option><option value="contacts">Hotel Contacts</option>
          <option value="vendors">Vendors</option><option value="vendor-contacts">Vendor Contacts</option>
          <option value="clients">Clients</option>
        </select>
        <input type="file" accept=".csv,.xlsx,.xls" onChange={onFile}/>
        <button className="btn-ghost sm" onClick={downloadTemplate} data-testid="download-template"><Download size={14}/> Template{kind==="hotels"?" (full workbook)":""}</button>
        {kind==="hotels" && file && !workbookSheets && <button className="btn-primary sm" onClick={doWorkbookImport} disabled={busy} data-testid="import-workbook"><Upload size={14}/> Import full workbook</button>}
        {kind==="hotels" && file && !workbookSheets && headers.length===0 && busy && <span className="muted">Reading workbook…</span>}
        {busy && !workbookSheets && <span className="muted">Working…</span>}
      </div>
      {workbookSheets && (
        <div style={{marginTop:14}}>
          <div className="panel-h"><h4 style={{margin:0}}>📚 Multi-sheet workbook detected</h4>
            <span className="muted small">All sheets will be imported in one shot — no column mapping needed.</span></div>
          <table className="tbl" style={{marginTop:8}}>
            <thead><tr><th style={{width:"30%"}}>Sheet</th><th style={{width:"15%"}}>Rows</th><th>Columns detected</th></tr></thead>
            <tbody>
              {workbookSheets.map(s => (
                <tr key={s.name}>
                  <td><b>{s.name}</b></td>
                  <td>{s.rows}</td>
                  <td className="truncate" style={{maxWidth:600, color:"#6b7280", fontSize:11}}>{s.columns.slice(0,12).join(", ")}{s.columns.length>12?"…":""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="row-btns" style={{marginTop:12}}>
            <button className="btn-primary" onClick={doWorkbookImport} disabled={busy} data-testid="import-workbook-main">
              <Upload size={14}/> Import full workbook ({workbookSheets.reduce((a,b)=>a+b.rows,0)} total rows)
            </button>
          </div>
          {result && result.hotels_inserted !== undefined && (
            <div className="muted" style={{marginTop:10, padding:"10px 12px", background:"var(--tint)", borderRadius:6}}>
              ✅ Hotels: <b>+{result.hotels_inserted} new</b>, <b>{result.hotels_updated} updated</b>
              {" • "}Spaces: <b>{result.spaces}</b>{" • "}Rooms: <b>{result.rooms}</b>{" • "}Dining: <b>{result.dining}</b>{" • "}Contacts: <b>{result.contacts}</b>
              {result.unlinked > 0 && <span style={{color:"var(--danger)"}}> • {result.unlinked} unlinked rows (venue_name didn't match)</span>}
            </div>
          )}
        </div>
      )}
      {!workbookSheets && headers.length>0 && (
        <div style={{marginTop:12}}>
          <div className="panel-h"><h4 style={{margin:0}}>Map columns ({total} rows)</h4>
            <span className="muted small">Auto-mapped {Object.values(mapping).filter(Boolean).length} of {headers.length}</span></div>
          <table className="tbl"><thead><tr><th style={{width:"25%"}}>Your Column</th><th style={{width:"25%"}}>→ System Field</th><th>Samples</th></tr></thead>
            <tbody>{headers.map(h=>(<tr key={h}>
              <td><b>{h}</b></td>
              <td><select value={mapping[h]||""} onChange={e=>setMapping({...mapping,[h]:e.target.value})}>
                <option value="">-- skip --</option>{sysFields.map(s=><option key={s} value={s}>{s}</option>)}</select></td>
              <td className="truncate" style={{maxWidth:340}}>{sample.map((r,i)=> r[h] ? <span key={i} style={{marginRight:8, color:"#6b7280"}}>{String(r[h]).slice(0,40)}</span> : null)}</td></tr>))}</tbody>
          </table>
          <div className="row-btns" style={{marginTop:10}}>
            <button className="btn-primary" onClick={doImport} disabled={busy}><Upload size={14}/> Import {total} rows</button>
          </div>
          {result && <div className="muted" style={{marginTop:8}}>✅ Inserted <b>{result.inserted}</b> • Added <b>{result.new_lookups||0}</b> new dropdown values</div>}
        </div>)}
    </div>
  );
}

function ExportPanel() {
  const [modAfter, setModAfter] = useState("");
  const [cFrom, setCFrom] = useState(""); const [cTo, setCTo] = useState("");
  const dl = (kind, google=false) => {
    const tok = localStorage.getItem("be_token");
    const params = new URLSearchParams();
    if (modAfter) params.set("modified_after", modAfter);
    if (cFrom) params.set("created_from", cFrom);
    if (cTo) params.set("created_to", cTo);
    const path = google ? `/export/google-contacts/${kind}` : `/export/${kind}`;
    fetch(`${API}${path}?${params.toString()}`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => { if(!r.ok) throw new Error("Export failed"); return r.blob(); })
      .then(b=>{ const url = URL.createObjectURL(b); const a = document.createElement("a");
        a.href = url; a.download = google?`google-${kind}.csv`:`${kind}.csv`; a.click(); toast.success("Downloaded"); })
      .catch(e=>toast.error(e.message));
  };
  return (
    <div className="panel">
      <h3>Export Data (Admin only)</h3>
      <p className="muted small">Export full CSV or Google Contacts format. Apply date filters to export only what's changed.</p>
      <div className="form-grid">
        <Field label="Modified After"><input type="date" value={modAfter} onChange={e=>setModAfter(e.target.value)}/></Field>
        <Field label="Created From"><input type="date" value={cFrom} onChange={e=>setCFrom(e.target.value)}/></Field>
        <Field label="Created To"><input type="date" value={cTo} onChange={e=>setCTo(e.target.value)}/></Field>
      </div>
      <h4 style={{marginTop:16}}>Full Workbook (.xlsx — Hotels + Spaces + Rooms + Dining + Contacts, one file)</h4>
      <div className="row-btns" style={{flexWrap:"wrap"}}>
        <button className="btn-primary sm" data-testid="export-workbook" onClick={()=>{
          const tok = localStorage.getItem("be_token");
          const params = new URLSearchParams();
          if (modAfter) params.set("modified_after", modAfter);
          if (cFrom) params.set("created_from", cFrom);
          if (cTo) params.set("created_to", cTo);
          fetch(`${API}/export/hotels-workbook?${params.toString()}`, { headers: { Authorization: `Bearer ${tok}` } })
            .then(r => { if(!r.ok) throw new Error("Export failed"); return r.blob(); })
            .then(b=>{ const url = URL.createObjectURL(b); const a = document.createElement("a");
              a.href = url; a.download = "ivory_vine_hotels_workbook.xlsx"; a.click(); toast.success("Workbook downloaded"); })
            .catch(e=>toast.error(e.message));
        }}>
          <Download size={14}/> Hotels Workbook (.xlsx)
        </button>
        <span className="muted small">Round-trip safe — re-uploadable via Import</span>
      </div>

      <h4 style={{marginTop:16}}>Full CSV (system format)</h4>
      <div className="row-btns" style={{flexWrap:"wrap"}}>
        {["hotels","contacts","vendors","clients","quotes"].map(k=>(
          <button key={k} className="btn-primary sm" onClick={()=>dl(k)}><Download size={14}/> {k}</button>
        ))}
      </div>
      <h4 style={{marginTop:16}}>Google Contacts CSV</h4>
      <div className="row-btns">
        <button className="btn-secondary sm" onClick={()=>dl("hotel", true)}><Download size={14}/> Hotel Contacts</button>
        <button className="btn-secondary sm" onClick={()=>dl("vendor", true)}><Download size={14}/> Vendor Contacts</button>
        <button className="btn-secondary sm" onClick={()=>dl("client", true)}><Download size={14}/> Clients</button>
      </div>
      <p className="muted small" style={{marginTop:8}}>Import the Google CSV at: Google Contacts → Import → Upload file.</p>
    </div>
  );
}

function StatsPanel() {
  const [s,setS] = useState({});
  useEffect(()=>{ api.get("/stats").then(r=>setS(r.data)).catch(()=>{}); },[]);
  return (
    <div className="panel">
      <h3>Database Stats</h3>
      <div className="stat-grid">
        {Object.entries(s).map(([k,v])=>(<div key={k} className="stat-box"><div className="stat-n">{v}</div><div className="stat-l">{k.replace(/_/g," ")}</div></div>))}
      </div>
    </div>
  );
}

// ===== App =====
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="bottom-right" richColors duration={2500}/>
        <Routes>
          <Route path="/login" element={<Login/>}/>
          <Route path="/" element={<Protected><Layout><Hotels/></Layout></Protected>}/>
          <Route path="/contacts" element={<Protected><Layout><ContactsPage/></Layout></Protected>}/>
          <Route path="/vendors" element={<Protected><Layout><Vendors/></Layout></Protected>}/>
          <Route path="/clients" element={<Protected><Layout><Clients/></Layout></Protected>}/>
          <Route path="/quotes" element={<Protected><Layout><QuotesPage/></Layout></Protected>}/>
          <Route path="/scan-inbox" element={<Protected><Layout><ScanInbox/></Layout></Protected>}/>
          <Route path="/calendar" element={<Protected><Layout><CalendarPage/></Layout></Protected>}/>
          <Route path="/map" element={<Protected><Layout><MapPage/></Layout></Protected>}/>
          <Route path="/settings" element={<Protected><Layout><Settings/></Layout></Protected>}/>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
